/**
 * kordAI 4성부 화성 분석 (backend/harmony_engine.py 와 동일 규칙, 로컬 전용).
 */

import { BEAT_VALUES, getBeatsPerMeasure } from './musicTheory.js'
import { CHORD_SYMBOL_I_MODE_MIX_B64, isChordSymbolIB64Figure } from './chordSymbols.js'
import { intervalBetween } from './intervalTheory.js'

/** 부동소수·박 경계에서 인접 구간이 잘못 잡히지 않게 */
const BEAT_SAMPLE_EPS = 1e-5

const NOTE_PC = {
  C: 0, 'C#': 1, Db: 1, D: 2, 'D#': 3, Eb: 3, E: 4, F: 5, 'F#': 6, Gb: 6,
  G: 7, 'G#': 8, Ab: 8, A: 9, 'A#': 10, Bb: 10, B: 11,
}

const KEY_TO_TONIC_PC = {
  C: 0, G: 7, D: 2, A: 9, E: 4, B: 11, 'F#': 6, F: 5, Bb: 10, Eb: 3, Ab: 8, Db: 1,
}

const PITCH_RE = /^([A-G])(##|#|bb|b|n)?(\d+)$/
const LETTER_PC = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 }
const ACC_SEMITONES = { '': 0, n: 0, '#': 1, '##': 2, b: -1, bb: -2 }

const TRIAD_INTERVALS = new Map([
  ['0,4,7', ['maj', 'triad']],
  ['0,3,7', ['min', 'triad']],
  ['0,3,6', ['dim', 'triad']],
  // 장3+감5 (이동 끝음의 가락적 #3 등) — ♭III 조의 vii°와 동일 음집합
  ['0,4,6', ['dim', 'triad']],
  ['0,4,8', ['aug', 'triad']],
])

const SEVENTH_INTERVALS = new Map([
  ['0,4,7,10', ['dom7', '7']],
  ['0,3,7,10', ['min7', '7']],
  ['0,4,7,11', ['maj7', '7']],
  ['0,3,6,10', ['half_dim7', '7']],
  ['0,3,6,9', ['dim7', '7']],
  // 증속7화음(백병동 제6장 §3): 속7화음의 5음이 상향변화(증5) → V+7/부V+7
  ['0,4,8,10', ['dom7s5', '7']],
])

/**
 * 속9화음 (백병동 제6장): 5음 의무 생략 → 근·3·7·9 = {0,4,10,2}.
 * 5음이 모두 울리는 완전형은 SATB에서 드묾.
 */
const NINTH_INTERVALS = new Map([
  ['0,2,4,10', ['dom9', '9']],       // 5음 생략 (백병동 표준 4성부)
  ['0,2,7,10', ['dom9', '9']],       // 3음 생략·5음 유지 (실습에서 흔함)
  ['0,2,4,7,10', ['dom9', '9']],    // 완전 5음
])

/**
 * 속11화음 (백병동 제6장): 6음 중 3·5음 의무 생략 → 근·7·9·11 = {0,10,2,5}.
 */
const ELEVENTH_INTERVALS = new Map([
  ['0,2,5,10', ['dom11', '11']],           // 3·5 생략 (백병동 표준 4성부)
  ['0,2,4,5,10', ['dom11', '11']],         // 3음 유지 (비표준)
  ['0,2,5,7,10', ['dom11', '11']],         // 5음 유지 (비표준)
  ['0,2,4,5,7,10', ['dom11', '11']],       // 완전형
])

/** 3화음 5음 생략 (근음+3음만 존재) */
const OMITTED_FIFTH_TRIADS = new Map([
  ['0,4', ['maj', 'triad']],
  ['0,3', ['min', 'triad']],
])

/** 7화음 (5음 생략) — backend/harmony_engine.py 와 동일 */
const OMITTED_FIFTH_SEVENTHS = new Map([
  ['0,4,10', ['dom7', '7']],
  ['0,3,10', ['min7', '7']],
  ['0,3,9', ['dim7', '7']],
  ['0,7,11', ['maj7', '7']],  // maj7 3음 생략 (e.g. C G B)
])

/** 3음이 생략된 패턴 — 이탤릭 표기 대상 */
const OMITTED_THIRD_KEYS = new Set(['0,7,11'])

// 부속7화음 대상 도수 — detectTonicization과 omitted-5th vii∅7 재해석에서 공용.
// 장조: II(2), iii(4), IV(5), V(7), vi(9) — 오프셋 기준. 단조: III(3), iv(5), V(7), VI(8), VII(10).
const SECONDARY_TARGETS_MAJOR = [[2, 'ii'], [4, 'iii'], [5, 'IV'], [7, 'V'], [9, 'vi']]
const SECONDARY_TARGETS_MINOR = [[3, 'III'], [5, 'iv'], [7, 'V'], [8, 'VI'], [10, 'VII']]

/**
 * 2차 기능 화음(Tonicization) 감지.
 * 대상: dom7(V7/X), half_dim7(vii∅7/X), dim7(vii°7/X)
 * 슬래시 오른쪽 X는 현재 조의 diatonic 화음(ii~vi, I 제외, vii° 제외)만 허용.
 */
function detectTonicization(tonicPc, rootPc, quality, mode = 'major') {
  const SEC_TARGETS = mode === 'minor' ? SECONDARY_TARGETS_MINOR : SECONDARY_TARGETS_MAJOR

  let targetPc = null
  let secLabel = null

  if (quality === 'dom7') {
    targetPc = (rootPc + 5) % 12   // V7/X: X는 근음보다 완전4도 위
    secLabel = 'V7'
  } else if (quality === 'dom7s5') {
    targetPc = (rootPc + 5) % 12   // V+7/X (증속7화음)
    secLabel = 'V+7'
  } else if (quality === 'dom9') {
    targetPc = (rootPc + 5) % 12
    secLabel = 'V\u2079'
  } else if (quality === 'dom11') {
    targetPc = (rootPc + 5) % 12
    secLabel = 'V\u00b9\u00b9' // V¹¹
  } else if (quality === 'half_dim7') {
    targetPc = (rootPc + 1) % 12   // vii∅7/X: X는 근음보다 반음 위
    secLabel = 'vii∅7'
  } else if (quality === 'dim7') {
    targetPc = (rootPc + 1) % 12   // vii°7/X
    secLabel = 'vii°7'
  }

  if (targetPc == null) return null

  const offset = (targetPc - tonicPc + 12) % 12
  const found = SEC_TARGETS.find(([o]) => o === offset)
  if (!found) return null

  return `${secLabel}/${found[1]}`
}

const MAJOR_DEGREES = [
  [0, 'I', 'maj', 'I'],
  [2, 'ii', 'min', 'ii'],
  [4, 'iii', 'min', 'iii'],
  [5, 'IV', 'maj', 'IV'],
  [7, 'V', 'maj', 'V'],
  [9, 'vi', 'min', 'vi'],
  [11, 'vii°', 'dim', 'vii°'],
]

/**
 * 단조 기본 3화음 (화성단음계 기준: ♯7음 사용). 5도(딸림)·7도(이끔) 자리는
 * 자연단음계형(v·♭VII)과 화성단음계형(V·vii°)이 둘 다 실제로 쓰이므로 두 항목 병기.
 */
const MINOR_DEGREES = [
  [0, 'i', 'min', 'i'],
  [2, 'ii°', 'dim', 'ii°'],
  [3, 'III', 'maj', 'III'],
  [5, 'iv', 'min', 'iv'],
  [7, 'v', 'min', 'v'],       // 자연단음계 딸림화음(단3화음)
  [7, 'V', 'maj', 'V'],       // 화성단음계 딸림화음(이끔음 올림 → 장3화음)
  [8, 'VI', 'maj', 'VI'],
  [10, 'VII', 'maj', 'VII'],  // 자연단음계 버금이끔음(subtonic)
  [11, 'vii°', 'dim', 'vii°'], // 화성단음계 이끔화음
]

/** "C" 같은 장조 표기 또는 "Am" 같은 단조 표기(으뜸음+소문자 m)를 [으뜸음 pc, 모드]로 분해. */
function parseKey(key) {
  const k = String(key ?? 'C').trim()
  if (k.endsWith('m') && k.slice(0, -1) in KEY_TO_TONIC_PC) {
    return [KEY_TO_TONIC_PC[k.slice(0, -1)], 'minor']
  }
  return [KEY_TO_TONIC_PC[k] ?? 0, 'major']
}

// kordAI 4성부 권장 음역 (MIDI) — theoryBase(baek/kostka)별로 다를 수 있음.
// baek: 백병동 교재 실측치(S C4–A5 / A G3–E5 / T C3–A4 / B E2–E4).
// kostka: 아직 Kostka & Payne 교재를 확인 전이라 잠정적으로 이전 기본값 유지 — 검토 후 갱신 예정.
const VOICE_RANGE_MIDI_BY_THEORY = {
  baek: {
    soprano: [60, 81], // C4–A5
    alto: [55, 76], // G3–E5
    tenor: [48, 69], // C3–A4
    bass: [40, 64], // E2–E4
  },
  kostka: {
    soprano: [60, 79], // C4–G5 (잠정치, 미검토)
    alto: [55, 72], // G3–C5 (잠정치, 미검토)
    tenor: [48, 67], // C3–G4 (잠정치, 미검토)
    bass: [40, 60], // E2–C4 (잠정치, 미검토)
  },
}
const VOICE_RANGE_MIDI = VOICE_RANGE_MIDI_BY_THEORY.baek // 하위호환용 기본값

const VOICE_LABEL_KO = {
  soprano: '소프라노',
  alto: '알토',
  tenor: '테너',
  bass: '베이스',
}

/** theoryBase별 성부법·표기 규칙 (docs/harmony-theory/baek_vs_kostka_comparison.md) */
export const THEORY_RULES = {
  baek: {
    allow_cadential_contrary_parallel_octave: true,
    check_outer_voice_convergence: true,
    check_cross_relation: true,
    unequal_fifths: 'baek_exceptions',
    close_position: 'upper_three',
    neapolitan_symbol: 'bII',
    closely_related: 'dominant_subdominant',
  },
  kostka: {
    allow_cadential_contrary_parallel_octave: false,
    check_outer_voice_convergence: false,
    check_cross_relation: false,
    unequal_fifths: 'bass_involved',
    close_position: 'soprano_tenor',
    neapolitan_symbol: 'N',
    closely_related: 'five_keys',
  },
}

export function getTheoryRules(theoryBase = 'baek') {
  return THEORY_RULES[theoryBase] ?? THEORY_RULES.baek
}

function normalizeTheoryBase(theoryBase) {
  return THEORY_RULES[theoryBase] ? theoryBase : 'baek'
}

function pitchToMidi(name) {
  const m = String(name).trim().match(PITCH_RE)
  if (!m) return null
  const pc = (((LETTER_PC[m[1]] + (ACC_SEMITONES[m[2] ?? ''] ?? 0)) % 12) + 12) % 12
  const octave = parseInt(m[3], 10)
  return (octave + 1) * 12 + pc
}

function firstNotePitchFromVoice(tokens) {
  if (!tokens?.length) return null
  for (const t of tokens) {
    if (t.startsWith('rest:')) continue
    if (t.includes(':')) return t.split(':', 1)[0].trim()
  }
  return null
}

/** 직렬화 토큰 `C4:q` / `rest:qr` 파싱 */
function parseVoiceToken(t) {
  const s = String(t).trim()
  if (s.startsWith('rest:')) {
    return { isRest: true, duration: s.slice(5).trim() || 'q' }
  }
  const idx = s.indexOf(':')
  if (idx < 0) return { isRest: true, duration: 'q' }
  return {
    isRest: false,
    pitch: s.slice(0, idx).trim(),
    duration: s.slice(idx + 1).trim() || 'q',
  }
}

/**
 * 한 성부 마디 토큰을 시간 구간으로 나눈다. (붙임줄 등으로 긴 음은 여러 박에 걸침)
 * @returns {Array<{ start: number, end: number, isRest: boolean, pitch: string | null }>}
 */
function voiceSegmentsFromTokens(tokens, beatsPerMeasure) {
  const segs = []
  let beat = 0
  const list = Array.isArray(tokens) ? tokens : []
  for (const raw of list) {
    if (beat >= beatsPerMeasure) break
    const p = parseVoiceToken(raw)
    const durBeats = BEAT_VALUES[p.duration] ?? 1
    const end = Math.min(beat + durBeats, beatsPerMeasure)
    segs.push({
      start: beat,
      end,
      isRest: p.isRest,
      pitch: p.isRest ? null : p.pitch,
    })
    beat = end
  }
  return segs
}

/**
 * 임의 시점 t(박 단위)에서 해당 성부 세그먼트의 음을 반환한다.
 */
function notesAtSampleTime(segments, t) {
  for (const s of segments) {
    if (s.start <= t + BEAT_SAMPLE_EPS && t < s.end + BEAT_SAMPLE_EPS) {
      if (s.isRest || !s.pitch) return []
      return [`${s.pitch}:q`]
    }
  }
  return []
}

function inversionRankTriad(bassPc, rootPc) {
  if (bassPc == null) return 99
  const rel = (bassPc - rootPc + 12) % 12
  if (rel === 0) return 0
  if (rel === 3 || rel === 4) return 1
  if (rel === 7) return 2
  if (rel === 6) return 2
  if (rel === 8) return 2
  return 5
}

function inversionRankSeventh(bassPc, rootPc, q) {
  if (bassPc == null) return 99
  const rel = (bassPc - rootPc + 12) % 12
  const order = { 0: 0, 3: 1, 4: 1, 7: 2, 10: 3, 11: 3, 6: 2, 9: 3, 8: 2 }
  if (q === 'half_dim7' && rel === 10) return 3
  return order[rel] ?? 5
}

function intervalKeyFromRoot(pcsSet, root) {
  const rel = [...pcsSet].map((p) => (p - root + 12) % 12)
  return [...new Set(rel)].sort((a, b) => a - b).join(',')
}

function matchChordPatterns(pcsSet) {
  const n = pcsSet.size
  const out = []
  for (let root = 0; root < 12; root++) {
    const key = intervalKeyFromRoot(pcsSet, root)
    if (n >= 4 && ELEVENTH_INTERVALS.has(key)) {
      const [q, k] = ELEVENTH_INTERVALS.get(key)
      out.push([root, q, k])
    } else if (n >= 4 && NINTH_INTERVALS.has(key)) {
      const [q, k] = NINTH_INTERVALS.get(key)
      out.push([root, q, k])
    } else if (n >= 4 && SEVENTH_INTERVALS.has(key)) {
      const [q, k] = SEVENTH_INTERVALS.get(key)
      out.push([root, q, k])
    } else if (n === 3) {
      if (TRIAD_INTERVALS.has(key)) {
        const [q, k] = TRIAD_INTERVALS.get(key)
        out.push([root, q, k])
      } else if (OMITTED_FIFTH_SEVENTHS.has(key)) {
        const [q, k] = OMITTED_FIFTH_SEVENTHS.get(key)
        out.push([root, q, k, OMITTED_THIRD_KEYS.has(key)])
      }
    } else if (n === 2) {
      if (OMITTED_FIFTH_TRIADS.has(key)) {
        const [q, k] = OMITTED_FIFTH_TRIADS.get(key)
        out.push([root, q, k])
      }
    }
  }
  return out
}

/**
 * 패턴 테이블에 없어도, 근음+7음+9음(+3 또는 5)이면 속9로 본다.
 * 백병동: 5음 생략이 원칙이나, 3음 생략 배치도 실습에서 자주 나옴.
 * (11음이 있으면 속11 — detectDominantEleventh 우선)
 */
function detectDominantNinth(pcsSet, bassPc) {
  if (pcsSet.size < 4) return null
  const roots = []
  for (let root = 0; root < 12; root++) {
    if (!pcsSet.has(root)) continue
    const has3 = pcsSet.has((root + 4) % 12)
    const has5 = pcsSet.has((root + 7) % 12)
    const has7 = pcsSet.has((root + 10) % 12)
    const has9 = pcsSet.has((root + 2) % 12)
    const has11 = pcsSet.has((root + 5) % 12)
    if (!has7 || !has9) continue
    if (has11) continue // 속11로 넘김
    if (!has3 && !has5) continue
    roots.push(root)
  }
  if (roots.length === 0) return null
  if (bassPc != null) {
    const bassRoot = roots.find((r) => r === bassPc)
    if (bassRoot != null) return [bassRoot, 'dom9', '9']
  }
  return [roots[0], 'dom9', '9']
}

/**
 * 근+7+9+11이면 속11. 백병동: 3·5음 생략이 원칙.
 */
function detectDominantEleventh(pcsSet, bassPc) {
  if (pcsSet.size < 4) return null
  const roots = []
  for (let root = 0; root < 12; root++) {
    if (!pcsSet.has(root)) continue
    const has7 = pcsSet.has((root + 10) % 12)
    const has9 = pcsSet.has((root + 2) % 12)
    const has11 = pcsSet.has((root + 5) % 12)
    if (!has7 || !has9 || !has11) continue
    roots.push(root)
  }
  if (roots.length === 0) return null
  if (bassPc != null) {
    const bassRoot = roots.find((r) => r === bassPc)
    if (bassRoot != null) return [bassRoot, 'dom11', '11']
  }
  return [roots[0], 'dom11', '11']
}

function pickRootAndQuality(_pcsSet, bassPc, candidates) {
  if (candidates.length === 0) return null
  if (candidates.length === 1) return candidates[0]

  function score([rootPc, q, k]) {
    // 구체적 화음 우선: 11 > 9 > 7 > triad
    const kindPenalty = k === '7' ? 10 : k === '9' ? 5 : 0
    const qForRank = (q === 'dom9' || q === 'dom11') ? 'dom7' : q
    const rank = k === '7' || k === '9' || k === '11'
      ? inversionRankSeventh(bassPc, rootPc, qForRank)
      : inversionRankTriad(bassPc, rootPc)
    const bassMatch = bassPc != null && (bassPc - rootPc) % 12 === 0 ? 0 : 1
    // 속9·속11은 근음위치 위주 (백병동)
    const tallBassPenalty = (k === '9' || k === '11') && bassMatch !== 0 ? 5 : 0
    return [kindPenalty + rank + tallBassPenalty, bassMatch, rootPc]
  }

  let best = candidates[0]
  let bestS = score(best)
  for (let i = 1; i < candidates.length; i++) {
    const t = candidates[i]
    const s = score(t)
    if (
      s[0] < bestS[0] ||
      (s[0] === bestS[0] && s[1] < bestS[1]) ||
      (s[0] === bestS[0] && s[1] === bestS[1] && s[2] < bestS[2])
    ) {
      best = t
      bestS = s
    }
  }
  return best
}

function inversionLabel(kind, bassPc, rootPc) {
  if (bassPc == null) return 'unknown'
  const rel = (bassPc - rootPc + 12) % 12
  if (kind === '9' || kind === '11') {
    if (rel === 0) return 'root position'
    return `inversion (bass +${rel} semitones)`
  }
  if (kind === '7') {
    if (rel === 0) return 'root position'
    if (rel === 3 || rel === 4) return 'first inversion'
    // 완전5(7) · 감5(6, dim7/ø7의 5음) · 증5(8, 증속7의 5음)
    if (rel === 7 || rel === 6 || rel === 8) return 'second inversion'
    // 단7(10) · 장7(11) · 감7(9)
    if (rel === 10 || rel === 11 || rel === 9) return 'third inversion'
    return `inversion (bass +${rel} semitones)`
  }
  if (rel === 0) return 'root position'
  if (rel === 3 || rel === 4) return 'first inversion'
  if (rel === 7 || rel === 6 || rel === 8) return 'second inversion'
  return 'root position'
}

/** 테르시안 전위 → 피겨 숫자(유니코드 첨자); 로마 문자열 뒤에 붙인다 */
function inversionFigure(kind, bassPc, rootPc) {
  if (bassPc == null) return ''
  const rel = (bassPc - rootPc + 12) % 12
  // 속9·속11: 표기에 이미 ⁹/¹¹ — 추가 피겨는 붙이지 않음
  if (kind === '9' || kind === '11') return ''
  if (kind === '7') {
    if (rel === 0) return '\u2077'
    if (rel === 3 || rel === 4) return '\u2076\u2085' // ⁶₅
    if (rel === 7 || rel === 6 || rel === 8) return '\u2074\u2083' // ⁴₃ (감5·증5 베이스 포함)
    if (rel === 10 || rel === 11 || rel === 9) return '\u2074\u2082' // ⁴₂ (감7 포함)
    return ''
  }
  if (rel === 0) return '\u2075\u2083'
  if (rel === 3 || rel === 4) return '\u2076\u2083'
  if (rel === 7 || rel === 6 || rel === 8) return '\u2076\u2084'
  return ''
}

/**
 * 전위 피겨(⁶₅·⁴₃ 등)가 붙을 때 로마 뒤의 루트위치 '7'은 중복이므로 제거한다.
 * 예: V7 + ⁶₅ → V⁶₅ (V₇⁶₅ 아님)
 */
function stripTrailingChordal7ForFiguredInversion(display, kind, invFig) {
  if (kind !== '7' || !invFig || invFig === '\u2077') return display
  if (display.endsWith('°7')) return display.slice(0, -1)
  if (display.endsWith('∅7') || display.endsWith('ø7')) return display.slice(0, -1)
  if (/7$/.test(display)) return display.slice(0, -1)
  return display
}

/** 로마(또는 V7/ii 등 슬래시 왼쪽) 바로 뒤에 전위 피겨를 이어 붙임 */
function inlineInversionFigure(display, kind, invFig) {
  if (!invFig) return display
  if (isChordSymbolIB64Figure(display)) return display
  if (display.startsWith('Ger.') || display.startsWith('Fr.') || display.startsWith('It.')) return display
  const slash = display.indexOf('/')
  if (slash >= 0) {
    const left = display.slice(0, slash)
    const right = display.slice(slash)
    return inlineInversionFigure(left, kind, invFig) + right
  }
  if (kind === '7' && invFig === '\u2077') {
    if (display.includes('maj7') || display.includes('\u2205') || display.includes('\u00f8') || display.includes('°7')) {
      return display
    }
    if (/7$/.test(display)) return display
  }
  return stripTrailingChordal7ForFiguredInversion(display, kind, invFig) + invFig
}

/** 장조에서 원래 대(大)3화음 도수(IV, V, I)가 단3화음으로 쓰인 mode mixture → 소문자 로마만 */
function modeMixtureLowerRomanTriad(sym) {
  if (sym === 'IV') return 'iv'
  if (sym === 'V') return 'v'
  if (sym === 'I') return 'i'
  return null
}

function romanSeventh(sym, step, quality) {
  if (quality === 'half_dim7' && step === 2) return 'ii∅7'
  if (quality === 'half_dim7' && step === 11) return 'vii∅7'
  if (quality === 'dim7' && step === 11) return 'vii°7'
  if (quality === 'dom7') return `${sym}7`
  if (quality === 'dom7s5') return `${sym}+7`
  if (quality === 'maj7') return `${sym}7`
  if (quality === 'min7') return `${sym}7`
  if (quality === 'half_dim7') return `${sym}∅7`
  return `${sym}7`
}

/** 속9 — 백병동 V₉ 표기에 맞춰 위첨자 ⁹ */
function romanNinth(sym, quality) {
  if (quality === 'dom9') return `${sym}\u2079`
  return `${sym}9`
}

/** 속11 — V¹¹ */
function romanEleventh(sym, quality) {
  if (quality === 'dom11') return `${sym}\u00b9\u00b9`
  return `${sym}11`
}

function romanMajor(tonicPc, rootPc, quality, kind) {
  const d = (rootPc - tonicPc + 12) % 12
  let matched = null
  for (const row of MAJOR_DEGREES) {
    const [step] = row
    if (d === step) {
      matched = row
      break
    }
  }

  if (matched) {
    const [step, , exp, sym] = matched
    if (kind === '11') return romanEleventh(sym, quality)
    if (kind === '9') return romanNinth(sym, quality)
    if (kind === '7') return romanSeventh(sym, step, quality)
    if (quality === 'maj' && exp === 'maj') return sym
    if (quality === 'min' && exp === 'min') return sym
    if (quality === 'dim' && exp === 'dim') return sym
    if (kind === 'triad' && quality === 'dim' && step === 2) return 'vii°/bIII'
    if (quality === 'aug') return sym.replace('°', '') + '+'
    if (kind === 'triad' && quality === 'min' && exp === 'maj') {
      const low = modeMixtureLowerRomanTriad(sym)
      if (low) return low
    }
    // 장조: 온음계상 단3화음·감3화음 도수(ii·iii·vi·vii°) 위 장3화음 = 부속딸림 3화음
    // (백병동·Kostka: V/x). vii°(11) 자리 장3화음은 V/iii(이끔음이 곧 iii의 딸림음).
    // "vii°(maj)" 같은 폴백 표기는 교재에 없음.
    if (kind === 'triad' && quality === 'maj' && (exp === 'min' || exp === 'dim')) {
      const secDomTriad = { 2: 'V/V', 4: 'V/vi', 9: 'V/ii', 11: 'V/iii' }
      if (secDomTriad[step]) return secDomTriad[step]
    }
    return `${sym}(${quality})`
  }

  if (kind === 'triad' && quality === 'maj') {
    const flatMajorBorrow = { 1: 'bII', 3: 'bIII', 8: 'bVI', 10: 'bVII' }
    if (d in flatMajorBorrow) return flatMajorBorrow[d]
  }

  if (kind === '7' && quality === 'dom7') {
    const flatDom7 = { 1: 'bII7', 3: 'bIII7', 8: 'bVI7', 10: 'bVII7' }
    if (d in flatDom7) return flatDom7[d]
  }

  if (kind === '9' && quality === 'dom9') {
    // 비온음계 근음의 속9 — 드묾
    return `?\u2079`
  }

  if (kind === '11' && quality === 'dom11') {
    return `?\u00b9\u00b9`
  }

  let best = null
  for (const [step, , , sym] of MAJOR_DEGREES) {
    const du = (d - step + 12) % 12
    const dd = (step - d + 12) % 12
    let dist
    let useSharp
    if (du <= dd) {
      dist = du
      useSharp = true
    } else {
      dist = dd
      useSharp = false
    }
    const acc = useSharp ? '♯' : '♭'
    if (best == null || dist < best[0]) best = [dist, sym, acc]
  }
  if (!best) return '?'
  const [, sym, acc] = best
  if (kind === '7') {
    let suf = '7'
    if (quality === 'maj7') suf = '7'
    else if (quality === 'half_dim7') suf = 'ø7'
    else if (quality === 'dim7') suf = '°7'
    return `${acc}${sym}${suf}`
  }
  return `${acc}${sym}`
}

const MINOR_MAJOR_ISH_QUALITIES = new Set(['maj', 'dom7', 'dom7s5', 'maj7', 'dom9', 'dom11', 'aug'])

/**
 * MINOR_DEGREES에서 도수(step)에 맞는 [기대품질, 로마기호] 한 쌍을 고른다.
 * 5도(딸림) 자리는 v(자연단음계)/V(화성단음계) 두 후보가 있어 quality로 어느 쪽인지 가른다.
 */
function pickMinorDegreeRow(step, quality) {
  const rows = MINOR_DEGREES.filter(([s]) => s === step).map(([, , exp, sym]) => [exp, sym])
  if (rows.length === 0) return null
  if (rows.length === 1) return rows[0]
  const majorIsh = MINOR_MAJOR_ISH_QUALITIES.has(quality)
  const pick = rows.find(([exp]) => (exp === 'maj') === majorIsh)
  return pick ?? rows[0]
}

/** 단조(화성단음계 기준) 로마숫자. 비다이아토닉은 ♯/♭ 접두. */
function romanMinor(tonicPc, rootPc, quality, kind) {
  const d = (rootPc - tonicPc + 12) % 12
  const row = pickMinorDegreeRow(d, quality)

  if (row) {
    const [exp, sym] = row
    if (kind === '11') return romanEleventh(sym, quality)
    if (kind === '9') return romanNinth(sym, quality)
    if (kind === '7') return romanSeventh(sym, d, quality)
    if (exp === quality) return sym
    // 단조 으뜸 자리 장3화음 = 피카르디 3도(장조에서 차용한 종지 효과)
    if (d === 0 && quality === 'maj') return 'I'
    if (quality === 'aug') return sym.replace('°', '') + '+'
    // 단조 이끔화음(vii°, 11) 자리 장3화음 = V/III(이끔음이 곧 나란한장조 III의 딸림음).
    if (kind === 'triad' && quality === 'maj' && d === 11) return 'V/III'
    return `${sym}(${quality})`
  }

  // 나폴리(♭II) — 단조에서 흔한 반음계적 차용화음(장3화음). III·VI·VII는 이미 단조
  // 자체의 다이어토닉 도수라 별도 처리 불필요, ♭II(도수 1)만 장조식 표기를 그대로 씀.
  if (kind === 'triad' && quality === 'maj' && d === 1) return 'bII'
  if (kind === '7' && quality === 'dom7' && d === 1) return 'bII7'

  let best = null
  const seenSteps = new Set()
  for (const [step, , , sym] of MINOR_DEGREES) {
    if (seenSteps.has(step)) continue
    seenSteps.add(step)
    const du = (d - step + 12) % 12
    const dd = (step - d + 12) % 12
    let dist
    let useSharp
    if (du <= dd) {
      dist = du
      useSharp = true
    } else {
      dist = dd
      useSharp = false
    }
    const acc = useSharp ? '♯' : '♭'
    if (best == null || dist < best[0]) best = [dist, sym, acc]
  }
  if (!best) return '?'
  const [, sym, acc] = best
  if (kind === '7') {
    let suf = '7'
    if (quality === 'maj7') suf = 'maj7'
    else if (quality === 'half_dim7') suf = 'ø7'
    else if (quality === 'dim7') suf = '°7'
    return `${acc}${sym}${suf}`
  }
  return `${acc}${sym}`
}

function romanNumeral(tonicPc, rootPc, quality, kind, mode = 'major') {
  return mode === 'minor' ? romanMinor(tonicPc, rootPc, quality, kind) : romanMajor(tonicPc, rootPc, quality, kind)
}

/** backend/harmony_engine.py의 _tonic_triad_root_doubling_issue와 동일 (메시지·조건 포함). */
function tonicTriadRootDoublingIssue(tonicPc, rootPc, quality, kind, midis) {
  if (rootPc == null || kind !== 'triad' || (quality !== 'maj' && quality !== 'min')) return null
  if ((rootPc - tonicPc + 12) % 12 !== 0) return null
  let rootCount = 0
  for (const v of ['soprano', 'alto', 'tenor', 'bass']) {
    const m = midis[v]
    if (m != null && m % 12 === rootPc) rootCount++
  }
  if (rootCount < 2) return '주3화음 근음 중복 없음: not good'
  return null
}

/** N6/♭II⁶ 3음 중복 권장 (Baek 후반·Kostka 공통). */
function neapolitanThirdDoublingIssue(tonicPc, rootPc, quality, kind, inversion, midis) {
  if (rootPc == null || kind !== 'triad' || quality !== 'maj') return null
  if ((rootPc - tonicPc + 12) % 12 !== 1) return null
  if (inversion !== 'first inversion') return null
  const thirdPc = (rootPc + 4) % 12
  let thirdCount = 0
  for (const v of ['soprano', 'alto', 'tenor', 'bass']) {
    const m = midis[v]
    if (m != null && m % 12 === thirdPc) thirdCount++
  }
  if (thirdCount < 2) return '나폴리6(N6/♭II⁶) 3음 중복 권장'
  return null
}

/**
 * 나폴리 1전위(♭II⁶₃)는 baek·Kostka 공통으로 N6(bII⁶₃) 형태로 표기(이론서 무관하게 확정된 표기 규칙).
 * 그 외 도수(근위 등)는 Kostka만 N 표기, baek은 bII 유지.
 */
function applyTheoryChordSymbol(chordSym, theoryBase) {
  if (chordSym.startsWith('bII')) {
    const figure = chordSym.slice(3)
    if (figure === '⁶₃') return `N6(${chordSym})` // 나폴리 6화음(1전위): N6(bII⁶₃)
    const rules = getTheoryRules(theoryBase)
    if (rules.neapolitan_symbol === 'N') return `N${figure}`
  }
  return chordSym
}

const QUALITY_FIFTH_INTERVAL = {
  maj: 7, min: 7, dim: 6, aug: 8,
  dom7: 7, min7: 7, maj7: 7, half_dim7: 6, dim7: 6, dom7s5: 8,
}

/**
 * 5음 중복 시 근음 중복을 권장(백병동 §2-3 중복 우선순위: 근음 > 5음).
 * 3화음 2전위(제2전위)는 베이스=5음 중복이 오히려 정석이라 예외로 둠.
 * backend/harmony_engine.py의 _fifth_doubling_issue와 동일.
 */
function fifthDoublingIssue(rootPc, quality, kind, inv, midis) {
  if (rootPc == null) return null
  const fifthInterval = QUALITY_FIFTH_INTERVAL[quality]
  if (fifthInterval == null) return null
  if (kind === 'triad' && inv === 'second inversion') return null
  const fifthPc = (rootPc + fifthInterval) % 12
  let count = 0
  for (const v of ['soprano', 'alto', 'tenor', 'bass']) {
    const m = midis[v]
    if (m != null && m % 12 === fifthPc) count++
  }
  if (count >= 2) return '5음 중복 (근음 중복 권장)'
  return null
}

/** backend/harmony_engine.py의 _triad_third_omission_issue와 동일 (메시지·조건 포함). */
function triadThirdOmissionIssue(rootPc, quality, kind, midis) {
  if (rootPc == null || kind !== 'triad') return null
  const thirdIntMap = { maj: 4, min: 3, dim: 3, aug: 4 }
  const thirdInt = thirdIntMap[quality]
  if (thirdInt == null) return null
  const thirdPc = (rootPc + thirdInt) % 12
  const altThirdPc = quality === 'dim' ? (rootPc + 4) % 12 : null
  let hasThird = false
  for (const v of ['soprano', 'alto', 'tenor', 'bass']) {
    const m = midis[v]
    if (m == null) continue
    const pc = m % 12
    if (pc === thirdPc || (altThirdPc != null && pc === altThirdPc)) {
      hasThird = true
      break
    }
  }
  if (!hasThird) return '3화음 3음 생략: bad'
  return null
}

/**
 * 속9: 백병동은 5음만 생략 가능. 3음(이끈음)이 없으면 지적.
 * @returns {{ issue: string, omittedThird: true } | null}
 */
function dominantNinthThirdOmission(rootPc, quality, kind, midis) {
  if (rootPc == null || kind !== '9' || quality !== 'dom9') return null
  const thirdPc = (rootPc + 4) % 12 // 속화음 3음 = 장3
  let hasThird = false
  for (const v of ['soprano', 'alto', 'tenor', 'bass']) {
    const m = midis?.[v]
    if (m != null && m % 12 === thirdPc) {
      hasThird = true
      break
    }
  }
  if (hasThird) return null
  return {
    issue: '속9화음 3음 생략: bad (5음만 생략 가능)',
    omittedThird: true,
  }
}

/**
 * 속11: 백병동은 3·5음 의무 생략. 남아 있으면 지적.
 * @returns {string[]}
 */
function dominantEleventhOmissionIssues(rootPc, quality, kind, midis) {
  if (rootPc == null || kind !== '11' || quality !== 'dom11') return []
  const thirdPc = (rootPc + 4) % 12
  const fifthPc = (rootPc + 7) % 12
  let hasThird = false
  let hasFifth = false
  for (const v of ['soprano', 'alto', 'tenor', 'bass']) {
    const m = midis?.[v]
    if (m == null) continue
    const pc = m % 12
    if (pc === thirdPc) hasThird = true
    if (pc === fifthPc) hasFifth = true
  }
  const issues = []
  if (hasThird) issues.push('속11화음 3음 미생략: bad (3·5음 생략)')
  if (hasFifth) issues.push('속11화음 5음 미생략: bad (3·5음 생략)')
  return issues
}

/**
 * 각 성부 첫 음이 권장 음역을 벗어나면 참고용으로 표시.
 * ⚠️ 이 범위를 벗어난다고 감점·오류로 취급하지 않음 — 어디까지나 권장 가이드일 뿐.
 */
function voiceRangeIssues(midis, theoryBase = 'baek') {
  const issues = []
  const ranges = VOICE_RANGE_MIDI_BY_THEORY[theoryBase] ?? VOICE_RANGE_MIDI_BY_THEORY.baek
  for (const k of Object.keys(ranges)) {
    const [lo, hi] = ranges[k]
    const m = midis[k]
    if (m == null) continue
    if (m < lo || m > hi) {
      issues.push(
        `${VOICE_LABEL_KO[k]} 음역 참고: MIDI ${m} (권장 ${lo}~${hi}, C4=60 기준 — 위반 아님)`,
      )
    }
  }
  return issues
}

/**
 * 상3성(소프라노·알토·테너) 인접 성부 간격은 옥타브 이내 권장 (Baek·Kostka 공통 연습 관례).
 * 테너–베이스는 예외라 여기서 검사하지 않음.
 */
function upperVoiceSpacingIssues(midis) {
  const issues = []
  const { soprano: s, alto: a, tenor: t } = midis
  if (s != null && a != null && s - a > 12) {
    issues.push('소프라노–알토 간격 옥타브 초과 (상3성 인접 성부는 옥타브 이내 권장)')
  }
  if (a != null && t != null && a - t > 12) {
    issues.push('알토–테너 간격 옥타브 초과 (상3성 인접 성부는 옥타브 이내 권장)')
  }
  return issues
}

/** 밀집/개리 — Baek: S–A–T 한 옥타브 안 / Kostka: S–T 옥타브 미만 */
function voicingPosition(midis, theoryBase = 'baek') {
  const s = midis.soprano
  const a = midis.alto
  const t = midis.tenor
  if (s == null || t == null) return null
  const rules = getTheoryRules(theoryBase)
  if (rules.close_position === 'soprano_tenor') {
    return Math.abs(s - t) < 12 ? 'close' : 'open'
  }
  if (a == null) return null
  return Math.max(s, a, t) - Math.min(s, a, t) <= 12 ? 'close' : 'open'
}

function isVToTonic(prevRoot, prevQuality, prevKind, currRoot, currQuality, currKind, tonicPc) {
  if (prevRoot == null || currRoot == null) return false
  const dominantPc = (tonicPc + 7) % 12
  const prevIsV = prevRoot === dominantPc && (
    (prevKind === 'triad' && prevQuality === 'maj')
    || (prevKind === '7' && prevQuality === 'dom7')
    || (prevKind === '9' && prevQuality === 'dom9')
    || (prevKind === '11' && prevQuality === 'dom11')
  )
  const currIsI = currKind === 'triad' && currRoot === tonicPc
    && (currQuality === 'maj' || currQuality === 'min')
  return Boolean(prevIsV && currIsI)
}

function parallelIssues(prev, curr, theoryBase = 'baek', { isAuthenticCadence = false } = {}) {
  const issues = []
  const rules = getTheoryRules(theoryBase)
  const voices = ['soprano', 'alto', 'tenor', 'bass']
  const pairs = []
  for (let i = 0; i < voices.length; i++) {
    for (let j = i + 1; j < voices.length; j++) pairs.push([voices[i], voices[j]])
  }
  for (const [v1, v2] of pairs) {
    const p1 = prev[v1]
    const p2 = prev[v2]
    const c1 = curr[v1]
    const c2 = curr[v2]
    if (p1 == null || p2 == null || c1 == null || c2 == null) continue
    const loP = p1 <= p2 ? p1 : p2
    const hiP = p1 <= p2 ? p2 : p1
    const loC = c1 <= c2 ? c1 : c2
    const hiC = c1 <= c2 ? c2 : c1
    const intP = (hiP - loP) % 12
    const intC = (hiC - loC) % 12
    // 완전5·8(동도)이 유지될 때만 병행 — 5도→8도 등은 병행이 아님
    if (![0, 7].includes(intP) || ![0, 7].includes(intC) || intP !== intC) continue
    const d1 = c1 - p1
    const d2 = c2 - p2
    if (d1 === 0 || d2 === 0) continue
    const sameDirection = (d1 > 0) === (d2 > 0)
    const n1 = VOICE_LABEL_KO[v1] ?? v1
    const n2 = VOICE_LABEL_KO[v2] ?? v2
    if (!sameDirection) {
      if (
        rules.allow_cadential_contrary_parallel_octave
        && isAuthenticCadence
        && new Set([v1, v2]).size === 2
        && ((v1 === 'soprano' && v2 === 'bass') || (v1 === 'bass' && v2 === 'soprano'))
        && intP === 0 && intC === 0
      ) {
        continue
      }
      const label = intP === 0 ? '8도' : '5도'
      issues.push(`${n1}–${n2} 반진행 병행 ${label} 의심`)
      continue
    }
    const label = intP === 0 ? '8도' : '5도'
    issues.push(`${n1}–${n2} 병행 ${label} 의심`)
  }
  return issues
}

/**
 * 은복5도·은복8도(hidden/covered parallel) — 백병동 기준 **외성(소프라노-베이스)에만 적용**.
 * 두 성부가 유사진행(같은 방향, 다른 폭)으로 완전5도/8도에 도달하고, 소프라노가 도약이면 의심.
 * 이미 직접병행(parallelIssues)으로 잡히는 경우는 제외.
 * backend/harmony_engine.py의 _hidden_parallel_issue와 동일.
 */
function hiddenParallelIssue(prev, curr) {
  const sPrev = prev.soprano
  const bPrev = prev.bass
  const sCurr = curr.soprano
  const bCurr = curr.bass
  if (sPrev == null || bPrev == null || sCurr == null || bCurr == null) return null
  const dS = sCurr - sPrev
  const dB = bCurr - bPrev
  if (dS === 0 || dB === 0) return null // 사진행 제외
  if ((dS > 0) !== (dB > 0)) return null // 반진행 제외
  const intervalC = Math.abs(sCurr - bCurr) % 12
  if (![0, 7].includes(intervalC)) return null
  const intervalP = Math.abs(sPrev - bPrev) % 12
  if (intervalP === intervalC) return null // 이미 직접병행 — 다른 규칙에서 처리
  if (Math.abs(dS) < 3) return null // 소프라노가 순차면 은복 아님(도약 조건)
  const label = intervalC === 0 ? '완전8도' : '완전5도'
  return `소프라노–베이스 은복${label} 의심 (소프라노 도약)`
}

/**
 * 외성(소프라노-베이스)이 반진행하다가 완전1도(유니즌)에서 만나는 것도 금지 (백병동).
 * 이미 유니즌이던 상태에서 유지되는 경우는 "새로 만나는" 게 아니므로 제외.
 * backend/harmony_engine.py의 _outer_voice_convergence_issue와 동일.
 */
function outerVoiceConvergenceIssue(prev, curr) {
  const sPrev = prev.soprano
  const bPrev = prev.bass
  const sCurr = curr.soprano
  const bCurr = curr.bass
  if (sPrev == null || bPrev == null || sCurr == null || bCurr == null) return null
  if (sPrev === bPrev) return null // 이미 유니즌이었음 — 새로 만난 게 아님
  const dS = sCurr - sPrev
  const dB = bCurr - bPrev
  if (dS === 0 || dB === 0) return null
  if ((dS > 0) === (dB > 0)) return null // 반진행이 아니면 해당 없음
  if (sCurr !== bCurr) return null // 완전1도(유니즌)로 만난 게 아님
  return '소프라노–베이스 반진행 유니즌 도달 (외성이 마주보고 만나는 진행 금지)'
}

const THIRD_INTERVAL_BY_QUALITY = { maj: 4, min: 3, dim: 3, aug: 4 }

/**
 * 감5도→완5도 병행 (Kostka "Unequal 5ths"에 대응; 백병동은 별칭 없이 서술).
 * baek: 두 예외 + 모든 성부쌍 / kostka: 베이스 관련만, Baek 예외 없음.
 */
function unequalFifthsIssue(
  prev, curr, prevRoot, prevQuality, prevKind, currRoot, currQuality, currKind, tonicPc,
  theoryBase = 'baek',
) {
  if (prevRoot == null || currRoot == null) return []

  const rules = getTheoryRules(theoryBase)
  const mode = rules.unequal_fifths

  const leadingTonePc = (tonicPc + 11) % 12
  const dominantPc = (tonicPc + 7) % 12
  const prevBass = prev.bass
  const currBass = curr.bass
  const prevBassPc = prevBass != null ? ((prevBass % 12) + 12) % 12 : null
  const currBassPc = currBass != null ? ((currBass % 12) + 12) % 12 : null

  if (mode === 'baek_exceptions') {
    const isException1 = (
      prevKind === '7' && prevQuality === 'dim7' && prevRoot === leadingTonePc
      && prevBassPc === leadingTonePc
      && currKind === 'triad' && (currQuality === 'maj' || currQuality === 'min') && currRoot === tonicPc
      && currBassPc === tonicPc
    )
    const fifthOfVPc = (dominantPc + 7) % 12
    const thirdOfIPc = currQuality in THIRD_INTERVAL_BY_QUALITY
      ? (tonicPc + THIRD_INTERVAL_BY_QUALITY[currQuality]) % 12
      : null
    const isException2 = (
      prevKind === '7' && prevQuality === 'dom7' && prevRoot === dominantPc
      && prevBassPc === fifthOfVPc
      && currKind === 'triad' && (currQuality === 'maj' || currQuality === 'min') && currRoot === tonicPc
      && thirdOfIPc != null && currBassPc === thirdOfIPc
    )
    if (isException1 || isException2) return []
  }

  const issues = []
  const voices = ['soprano', 'alto', 'tenor', 'bass']
  for (let i = 0; i < voices.length; i++) {
    for (let j = i + 1; j < voices.length; j++) {
      const v1 = voices[i]
      const v2 = voices[j]
      if (mode === 'bass_involved' && v1 !== 'bass' && v2 !== 'bass') continue
      const p1 = prev[v1]
      const p2 = prev[v2]
      const c1 = curr[v1]
      const c2 = curr[v2]
      if (p1 == null || p2 == null || c1 == null || c2 == null) continue
      const loP = p1 <= p2 ? p1 : p2
      const hiP = p1 <= p2 ? p2 : p1
      const loC = c1 <= c2 ? c1 : c2
      const hiC = c1 <= c2 ? c2 : c1
      const intP = (hiP - loP) % 12
      const intC = (hiC - loC) % 12
      if (intP !== 6 || intC !== 7) continue
      const d1 = c1 - p1
      const d2 = c2 - p2
      if (d1 === 0 || d2 === 0) continue
      if ((d1 > 0) !== (d2 > 0)) continue
      const n1 = VOICE_LABEL_KO[v1] ?? v1
      const n2 = VOICE_LABEL_KO[v2] ?? v2
      issues.push(`${n1}–${n2} 감5도→완5도 병행 금지`)
    }
  }
  return issues
}

const UPPER_VOICES = ['soprano', 'alto', 'tenor']

/**
 * 화음 진행 시 공통음이 있으면 유지하는 게 선호됨(백병동) — 상3성 기준.
 * 베이스는 오히려 도약이 권장되는 경우가 많아 대상에서 제외.
 * backend/harmony_engine.py의 _common_tone_retention_issue와 동일.
 */
function commonToneRetentionIssue(prev, curr) {
  const prevPcs = new Set(UPPER_VOICES.filter((v) => prev[v] != null).map((v) => ((prev[v] % 12) + 12) % 12))
  const currPcs = new Set(UPPER_VOICES.filter((v) => curr[v] != null).map((v) => ((curr[v] % 12) + 12) % 12))
  const commonPcs = new Set([...prevPcs].filter((pc) => currPcs.has(pc)))
  if (commonPcs.size === 0) return null

  for (const v of UPPER_VOICES) {
    const pm = prev[v]
    const cm = curr[v]
    if (pm != null && cm != null && pm === cm && commonPcs.has(((pm % 12) + 12) % 12)) {
      return null
    }
  }
  return '공통음이 있는데 상3성 어느 성부도 유지하지 않음 (공통음 유지 권장)'
}

/**
 * 증음정 진행 금지(백병동 §2-2) — 성부별로 이전→현재 선율 음정이 증(또는 겹증)이면 위반.
 * 스펠링(음이름)이 있어야 증/감을 구별할 수 있으므로 MIDI가 아닌 실제 음이름으로 판정.
 */
function augmentedMelodicIntervalIssues(prevPitches, currPitches) {
  const issues = []
  for (const v of ['soprano', 'alto', 'tenor', 'bass']) {
    const pPrev = prevPitches?.[v]
    const pCurr = currPitches?.[v]
    if (!pPrev || !pCurr || pPrev === pCurr) continue
    let result
    try {
      result = intervalBetween(pPrev, pCurr)
    } catch {
      continue
    }
    // 증1도(반음 임시표 변화, 예: F→F#)는 증음정 진행 금지 대상이 아님 —
    // 같은 음이름의 반음 변화일 뿐 도약형 증음정(증2도 이상)과는 다름.
    if (result.genericSize === 0) continue
    if (result.qualityKo === '증' || result.qualityKo === '겹증') {
      issues.push(`${VOICE_LABEL_KO[v] ?? v} 증음정 진행 금지: ${pPrev}→${pCurr} (${result.label})`)
    }
  }
  return issues
}

function pitchLetterPc(pitch) {
  if (!pitch) return null
  const lm = String(pitch).match(/^([A-G])/)
  if (!lm) return null
  const midi = pitchToMidi(pitch)
  if (midi == null) return null
  return [lm[1], ((midi % 12) + 12) % 12]
}

/**
 * 대사현상(교차관계, cross relation) 금지(백병동 §2-2) — 반음계적 성부진행 전반에 적용되는
 * 일반 규칙(특정 화음 진행에 한정되지 않음). 어떤 성부가 갖고 있던 음(예: C)이 다음 화음에서
 * 반음 변화(예: C#)해야 할 때, 그 반음 변화는 같은 성부 안에서 일어나야 한다 — 원래 그 음을
 * 갖고 있지 않던 다른 성부가 대신 반음 변화된 음을 맡으면 위반.
 * 같은 음이 여러 성부에 중복된 경우, 그중 한 성부라도 스스로 반음 변화를 맡았다면
 * (다른 중복 성부가 자유롭게 다른 곳으로 움직이더라도) 위반이 아니다.
 */
function crossRelationIssues(prevPitches, currPitches) {
  const voices = ['soprano', 'alto', 'tenor', 'bass']
  const prevLp = {}
  const currLp = {}
  for (const v of voices) {
    prevLp[v] = pitchLetterPc(prevPitches?.[v])
    currLp[v] = pitchLetterPc(currPitches?.[v])
  }

  const prevGroups = new Map() // key "letter:pc" -> [voice,...]
  for (const v of voices) {
    if (prevLp[v]) {
      const key = `${prevLp[v][0]}:${prevLp[v][1]}`
      if (!prevGroups.has(key)) prevGroups.set(key, [])
      prevGroups.get(key).push(v)
    }
  }
  const currGroups = new Map()
  for (const v of voices) {
    if (currLp[v]) {
      const key = `${currLp[v][0]}:${currLp[v][1]}`
      if (!currGroups.has(key)) currGroups.set(key, [])
      currGroups.get(key).push(v)
    }
  }

  const issues = []
  const seenEvents = new Set()
  for (const [prevKey, plainVoices] of prevGroups) {
    const [letter, pcPrevStr] = prevKey.split(':')
    const pcPrev = Number(pcPrevStr)
    for (const [currKey, altVoices] of currGroups) {
      const [letterW, pcCurrStr] = currKey.split(':')
      const pcCurr = Number(pcCurrStr)
      if (letterW !== letter || pcCurr === pcPrev) continue
      const diff = ((pcCurr - pcPrev) % 12 + 12) % 12
      if (Math.min(diff, 12 - diff) !== 1) continue
      if (prevGroups.has(`${letter}:${pcCurr}`)) continue // 이미 이전 화음에도 있던 음
      const event = `${letter}:${pcPrev}:${pcCurr}`
      if (seenEvents.has(event)) continue
      const resolvedInPlace = plainVoices.some((v) => {
        const cv = currLp[v]
        return cv && cv[0] === letter && cv[1] === pcCurr
      })
      if (resolvedInPlace) continue
      seenEvents.add(event)
      const plainLabel = plainVoices.map((v) => `${VOICE_LABEL_KO[v] ?? v}(${prevPitches[v]})`).join('·')
      const altLabel = altVoices.map((v) => `${VOICE_LABEL_KO[v] ?? v}(${currPitches[v]})`).join('·')
      issues.push(`대사현상(교차관계) 금지: ${plainLabel} → ${altLabel}`)
    }
  }
  return issues
}

function seventhIntervalForQuality(quality) {
  if (quality === 'dom7' || quality === 'min7' || quality === 'half_dim7') return 10
  if (quality === 'maj7') return 11
  if (quality === 'dim7') return 9
  return null
}

function chordalSeventhVoices(midisObj, rootPc, quality, kind) {
  if (kind !== '7' || rootPc == null) return []
  const sevInt = seventhIntervalForQuality(quality)
  if (sevInt == null) return []
  const sevPc = (rootPc + sevInt) % 12
  const voices = ['soprano', 'alto', 'tenor', 'bass']
  return voices.filter((v) => {
    const m = midisObj?.[v]
    return m != null && m % 12 === sevPc
  })
}

/** 7th prep: common tone (same MIDI or same pitch-class) or stepwise */
function isAcceptableSeventhPrepare(prevM, currM) {
  if (prevM == null || currM == null) return false
  if (prevM === currM) return true
  if (((prevM % 12) + 12) % 12 === ((currM % 12) + 12) % 12) return true
  const d = Math.abs(currM - prevM)
  return d === 1 || d === 2
}

function isValidSeventhResolution(currM, nextM) {
  if (currM == null || nextM == null) return false
  const d = nextM - currM
  if (d === -1 || d === -2 || d === 0) return true
  // 옥타브만 다른 동음(동일 pitch-class) 유지도 해결로 허용
  return ((nextM - currM) % 12 + 12) % 12 === 0
}

/**
 * 증6화음 감지 (장조 기준)
 * Italian (It.6):  ♭6 + 1 + ♯4          (3음)
 * French  (Fr.6):  ♭6 + 1 + 2 + ♯4      (4음)
 *   ♭6 bass → Fr.6 | ♯4 bass → Fr.⁶₅ | 1 bass → Fr.⁴₂ | 2 bass → Fr.⁷₅
 * German  (Ger.6): ♭6 + 1 + ♭3 + ♯4     (4음)
 *   ♭6 bass → Ger.6 | 1 bass → Ger.⁴₃ | ♭3 bass → Ger.⁴₂ | ♯4 bass → Ger.⁷₅
 *
 * 반환값: [라벨, 이 증6화음이 이끄는 딸림음의 으뜸음 pc, 이 증6화음의 근음(♭6 등) pc]
 * 근음 pc는 호출부에서 전위 라벨(root/first/second/third inversion) 계산에 쓰인다.
 */
const _AUG6_LETTER_IDX = { C:0, D:1, E:2, F:3, G:4, A:5, B:6 }

function detectAugmentedSixth(tonicPc, pcsSet, bassPc, pcToLetter = {}, mode = 'major') {
  const b6  = (tonicPc + 8) % 12  // ♭6
  const t   =  tonicPc             // 1 (tonic)
  const s2  = (tonicPc + 2) % 12  // 2
  const b3  = (tonicPc + 3) % 12  // ♭3
  const sh4 = (tonicPc + 6) % 12  // ♯4

  const has = (pc) => pcsSet.has(pc)
  const FR43 = 'Fr.⁴₃' // Fr.⁴₃ (부증6 전용 — 전위 미구별 고정 피겨)

  // ── Primary: 주조 증6화음 (bassPc로 전위 구별) ───────────────────────
  // German Aug 6th
  if (has(b6) && has(t) && has(b3) && has(sh4) && pcsSet.size === 4) {
    if (bassPc === sh4) return ['Ger.⁷₅', tonicPc, b6]
    if (bassPc === b3)  return ['Ger.⁴₂', tonicPc, b6]
    if (bassPc === t)   return ['Ger.⁴₃', tonicPc, b6]
    return ['Ger.6', tonicPc, b6]
  }

  // French Aug 6th
  if (has(b6) && has(t) && has(s2) && has(sh4) && pcsSet.size === 4) {
    if (bassPc === s2)  return ['Fr.⁷₅', tonicPc, b6]
    if (bassPc === t)   return ['Fr.⁴₂', tonicPc, b6]
    if (bassPc === sh4) return ['Fr.⁶₅', tonicPc, b6]
    return ['Fr.6', tonicPc, b6]
  }

  // Italian Aug 6th
  if (has(b6) && has(t) && has(sh4) && pcsSet.size === 3) {
    if (bassPc === sh4) return ['It.⁵₃', tonicPc, b6]
    if (bassPc === t)   return ['It.⁶₄', tonicPc, b6]
    return ['It.6', tonicPc, b6]
  }

  // ── Secondary: 부증6화음 (Tonicization) ─────────────────────────────
  const DIATONIC_ROMAN = mode === 'minor'
    ? { 0:'i', 2:'ii°', 3:'III', 5:'iv', 7:'V', 8:'VI', 10:'VII', 11:'vii°' }
    : { 0:'I', 2:'ii', 4:'iii', 5:'IV', 7:'V', 9:'vi', 11:'vii°' }
  const pcsArr = [...pcsSet]
  for (const a of pcsArr) {
    for (const bPc of pcsArr) {
      if (a === bPc) continue
      if ((bPc - a + 12) % 12 !== 10) continue
      const la = pcToLetter[a], lb = pcToLetter[bPc]
      if (la && lb && (_AUG6_LETTER_IDX[lb] - _AUG6_LETTER_IDX[la] + 7) % 7 !== 5) continue
      const impliedTonic = (a + 4) % 12
      const d = (impliedTonic - tonicPc + 12) % 12
      if (d === 0) continue
      if (!(d in DIATONIC_ROMAN)) continue
      const sc1  = impliedTonic
      const scB3 = (impliedTonic + 3) % 12
      const scS2 = (impliedTonic + 2) % 12
      const rem  = new Set(pcsArr.filter(p => p !== a && p !== bPc))

      let kind = null
      if (rem.size === 1 && rem.has(sc1)) {
        kind = 'It.⁶'
      } else if (rem.size === 2 && rem.has(sc1) && rem.has(scB3)) {
        kind = 'Ger.⁷₅'
      } else if (rem.size === 2 && rem.has(sc1) && rem.has(scS2)) {
        kind = FR43
      }
      if (kind) return [`${kind}/${DIATONIC_ROMAN[d]}`, impliedTonic, a]
    }
  }

  return null
}

/** KEY_TO_TONIC_PC 역방향 — 전조 이중 표기("C: v♭\nF: ii")용 조 이름. */
const PC_TO_KEY_LETTER = Object.fromEntries(Object.entries(KEY_TO_TONIC_PC).map(([k, v]) => [v, k]))

/**
 * 근음+품질이 해당 조(장/단) 온음계 3화음 도수와 맞아떨어지는지. 단조는 v/V 양쪽 다 인정.
 * (공통화음 전조 검출 전용 — 현재 전조 검출은 장조 소스만 다루므로 mode 기본값 'major'.)
 */
function isDiatonicTriadInKey(tonicPc, rootPc, quality, kind, mode = 'major') {
  if (rootPc == null || kind !== 'triad') return false
  const d = (rootPc - tonicPc + 12) % 12
  const table = mode === 'minor' ? MINOR_DEGREES : MAJOR_DEGREES
  return table.some(([step, , exp]) => step === d && exp === quality)
}

// 공통화음 전조 피벗 판정 전용: 7화음도 그 안에 담긴 3화음 성분(근음+3화음 품질)이 도수표와
// 맞으면 피벗으로 인정한다 — 예: Dm7은 C장조의 ii7이면서 동시에 Bb장조의 iii7이라, 3화음보다
// 오히려 더 확실한 공통화음이 된다(사용자 지침: 7화음도 피벗이 될 수 있다).
const SEVENTH_TO_TRIAD_QUALITY = { dom7: 'maj', min7: 'min', maj7: 'maj', half_dim7: 'dim', dim7: 'dim' }

function isDiatonicPivotChord(tonicPc, rootPc, quality, kind, mode = 'major') {
  if (rootPc == null) return false
  let effQuality
  if (kind === 'triad') {
    effQuality = quality
  } else if (kind === '7') {
    effQuality = SEVENTH_TO_TRIAD_QUALITY[quality]
    if (effQuality == null) return false
  } else {
    return false
  }
  const d = (rootPc - tonicPc + 12) % 12
  const table = mode === 'minor' ? MINOR_DEGREES : MAJOR_DEGREES
  return table.some(([step, , exp]) => step === d && exp === effQuality)
}

// 부속화음(딸림의 딸림 등, V/x)도 그 조 '안'의 화음으로 친다 — 근음이 그 조 어느
// 다이어토닉 도수(으뜸 제외)의 딸림 자리(도수+7)와 맞고 품질이 장3화음/속7화음이면 인정.
function isSecondaryDominantInKey(tonicPc, rootPc, quality, kind, mode = 'major') {
  if (rootPc == null) return false
  if (kind === 'triad' && quality !== 'maj') return false
  if (kind === '7' && quality !== 'dom7' && quality !== 'dom7s5') return false
  if (kind !== 'triad' && kind !== '7') return false
  const d = (rootPc - tonicPc + 12) % 12
  const table = mode === 'minor' ? MINOR_DEGREES : MAJOR_DEGREES
  const seenSteps = new Set()
  for (const [step] of table) {
    if (step === 0 || seenSteps.has(step)) continue
    seenSteps.add(step)
    if ((step + 7) % 12 === d) return true
  }
  return false
}

// 공통화음 전조 피벗의 마지막 수단 전용 — 순수 다이어토닉뿐 아니라 차용화음(나란한단조에서
// 빌려온 화음)·부속화음(V/x)까지 그 조 '안'의 화음으로 넓게 인정한다(사용자 지침: 3화음·
// 7화음·차용화음·부속화음 모두 피벗이 될 수 있다).
// 차용화음(나란한단조에서 빌려온 화음)은 일부러 여기 넣지 않는다 — 대상 조 쪽까지 나란한
// 단조 예외를 열어주면 "장3화음이면 거의 다 어딘가의 차용화음"이 되어버려서 오탐이
// 폭증한다(예: C장조 IV가 G장조의 나란한단조 bVII로 걸려 가짜 전조가 잡힘). 차용화음
// 피벗은 기존처럼 pivotViaParallelMode가 켜진 방향(♭III 등)에서만 쓴다.
function fitsKeyBroadly(tonicPc, rootPc, quality, kind, mode = 'major') {
  if (isDiatonicPivotChord(tonicPc, rootPc, quality, kind, mode)) return true
  if (isSecondaryDominantInKey(tonicPc, rootPc, quality, kind, mode)) return true
  return false
}

// ── 음계 지속성 기반 전조 검출 ──────────────────────────────────────────────
// 공통화음(피벗+정격종지) 방식과 별개인 두 번째 탐지 축. "전조는 음계가 바뀌는
// 것"(사용자 지침) — 어느 화음 하나의 자리바꿈·정격종지 여부와 무관하게, 실제로
// 불린 음들이 여러 마디에 걸쳐 다른 조의 온음계 안에만 안정적으로 머물면 그 자체로
// 전조로 본다. harmony_engine.py와 1:1 대응.
const MAJOR_SCALE_STEPS = [0, 2, 4, 5, 7, 9, 11]
const MINOR_SCALE_STEPS = [0, 2, 3, 5, 7, 8, 9, 10, 11]
const MIN_SCALE_RUN = 2
const MIN_SCALE_DISTANCE = 6
const ALL_SCALE_KEYS = []
for (let t = 0; t < 12; t++) {
  ALL_SCALE_KEYS.push([t, 'major'])
  ALL_SCALE_KEYS.push([t, 'minor'])
}

function scalePcs(tonicPc, mode) {
  const steps = mode === 'major' ? MAJOR_SCALE_STEPS : MINOR_SCALE_STEPS
  return new Set(steps.map((s) => (tonicPc + s) % 12))
}

function isSubset(a, b) {
  for (const x of a) if (!b.has(x)) return false
  return true
}

function scaleCandidatesForPcs(pcs) {
  if (pcs.size === 0) return new Set(ALL_SCALE_KEYS.map((k) => k.join(':')))
  const out = new Set()
  for (const [t, m] of ALL_SCALE_KEYS) {
    if (isSubset(pcs, scalePcs(t, m))) out.add(`${t}:${m}`)
  }
  return out
}

function scaleKeyDistance([t, m], activeTonic, activeMode) {
  const a = scalePcs(t, m)
  const b = scalePcs(activeTonic, activeMode)
  let diff = 0
  for (const x of a) if (!b.has(x)) diff++
  for (const x of b) if (!a.has(x)) diff++
  return diff
}

function pickBestScaleKey(commonKeys, activeTonic, activeMode) {
  let best = null
  let bestKey = null
  for (const k of commonKeys) {
    const [t, m] = k.split(':')
    const cand = [Number(t), m]
    const dist = scaleKeyDistance(cand, activeTonic, activeMode)
    const key = [dist, m === 'major' ? 0 : 1, cand[0]]
    if (
      bestKey === null ||
      key[0] < bestKey[0] ||
      (key[0] === bestKey[0] && key[1] < bestKey[1]) ||
      (key[0] === bestKey[0] && key[1] === bestKey[1] && key[2] < bestKey[2])
    ) {
      bestKey = key
      best = cand
    }
  }
  return best
}

/**
 * [lo,hi] 구간 전체가 아니라, 시작점을 최대 (hi-최소길이+1)까지 한 마디씩 당겨보며
 * 가장 좋은(거리 최소, 동률이면 장조, 그래도 동률이면 덜 당긴 쪽) 후보를 찾는다.
 * 전조 준비 구간의 첫 마디에 옛 조 잔류음이 섞여 있으면 그 마디 하나 때문에 진짜
 * 정답(보통 장조)이 통째로 후보에서 빠지고 훨씬 헐거운 단조 나란한조가 대신 뽑히는
 * 사고가 난다(회귀검사로 확인됨) — 첫 마디를 하나씩 떼어내며 다시 검사해서 막는다.
 */
function bestTrimmedScaleWindow(measurePcSets, lo, hi, activeTonic, activeMode) {
  let best = null // { sortKey, start, cand }
  const startLimit = hi - MIN_SCALE_RUN + 1
  for (let s = lo; s <= startLimit; s++) {
    let common = null
    for (let k = s; k <= hi; k++) {
      const cands = scaleCandidatesForPcs(measurePcSets[k])
      if (common === null) {
        common = cands
      } else {
        common = new Set([...common].filter((x) => cands.has(x)))
      }
      if (common.size === 0) break
    }
    if (!common || common.size === 0) continue
    common.delete(`${activeTonic}:${activeMode}`)
    const filtered = new Set(
      [...common].filter((k) => {
        const [t, m] = k.split(':')
        return scaleKeyDistance([Number(t), m], activeTonic, activeMode) >= MIN_SCALE_DISTANCE
      }),
    )
    if (filtered.size === 0) continue
    const cand = pickBestScaleKey(filtered, activeTonic, activeMode)
    const dist = scaleKeyDistance(cand, activeTonic, activeMode)
    const sortKey = [dist, cand[1] === 'major' ? 0 : 1, s]
    if (
      best === null ||
      sortKey[0] < best.sortKey[0] ||
      (sortKey[0] === best.sortKey[0] && sortKey[1] < best.sortKey[1]) ||
      (sortKey[0] === best.sortKey[0] && sortKey[1] === best.sortKey[1] && sortKey[2] < best.sortKey[2])
    ) {
      best = { sortKey, start: s, cand }
    }
  }
  return best ? { start: best.start, cand: best.cand } : null
}

/**
 * 마디별 실사용 음 집합만으로, 공통화음 방식과 독립적으로 전조를 찾는다.
 * tonicByMeasure/modeByMeasure는 공통화음 방식이 이미 확정한 마디별 활성 조를
 * 기준선으로 삼는다. primaryPivotMeasures 근처(전후 1마디)는 공통화음 방식에 양보한다.
 */
function detectScalePersistenceModulations(measurePcSets, tonicByMeasure, modeByMeasure, primaryPivotMeasures) {
  const n = measurePcSets.length
  if (n === 0) return []
  const pivotMeasures = primaryPivotMeasures || new Set()
  const events = []
  let activeTonic = tonicByMeasure[0]
  let activeMode = modeByMeasure[0]
  let i = 0
  while (i < n) {
    if (isSubset(measurePcSets[i], scalePcs(activeTonic, activeMode))) {
      i += 1
      continue
    }
    let common = scaleCandidatesForPcs(measurePcSets[i])
    let j = i
    while (j + 1 < n) {
      const nxt = scaleCandidatesForPcs(measurePcSets[j + 1])
      const inter = new Set([...common].filter((x) => nxt.has(x)))
      if (inter.size === 0) break
      common = inter
      j += 1
    }
    const runLen = j - i + 1
    const best = runLen >= MIN_SCALE_RUN ? bestTrimmedScaleWindow(measurePcSets, i, j, activeTonic, activeMode) : null
    if (best !== null) {
      const { start, cand } = best
      const [newTonic, newMode] = cand
      let overlapsPrimaryPivot = false
      for (let m = start - 1; m <= j + 1; m++) {
        if (pivotMeasures.has(m)) { overlapsPrimaryPivot = true; break }
      }
      if (!overlapsPrimaryPivot) {
        events.push({
          measureStart: start,
          oldTonicPc: activeTonic,
          oldMode: activeMode,
          newTonicPc: newTonic,
          newMode,
        })
        activeTonic = newTonic
        activeMode = newMode
        i = j + 1
        continue
      }
    }
    i += 1
  }
  return events
}

// ── 반음계적 전조 검출 ──────────────────────────────────────────────────────
// 세 번째 축(사용자 지침). 온음계적(공통화음) 전조와 달리 공통화음이 전혀 필요 없다 —
// 목표 조의 딸림7화음(혹은 딸림3화음)을 근친조 제한 없이 그냥 반음계적으로 꽂아 넣고
// 바로 그 조의 으뜸화음으로 해결하면(V7-I 혹은 V-I, 둘 다 근원위치) 그 자체로 전조가
// 확정된다. 부속화음(V/ii,V/iii,V/IV,V/V,V/vi — 지금 조 다이어토닉 화음으로 흔히
// 해결되는 5개)과 구별해야 한다: 해결된 화음이 지금 조 자체의 다이어토닉 3화음
// (으뜸음·감3화음 제외)과 근음·품질이 맞아떨어지면 그냥 흔한 부속화음일 뿐 전조가 아니다.
function isOrdinarySecondaryDominantTarget(tonicPc, rootPc, quality, mode) {
  if (rootPc === tonicPc) return false
  if (quality === 'dim') return false
  return isDiatonicTriadInKey(tonicPc, rootPc, quality, 'triad', mode)
}

/**
 * prev가 그 자체로 이미 "ct(새 조)의 근원위치 딸림(7)화음"이면서 옛 조 안에서 흔한
 * 화음으로는 설명 안 되는 색채화음인지 — 반음계적 전조의 정의 그 자체. 공통화음 탐색이
 * 이 경우까지 3순위(부속화음·차용화음) 최후수단으로 억지로 피벗을 찾아버리는 걸 막는 데
 * 쓴다 — prev 혼자만으로 이미 반음계적 전조가 설명되면, 그보다 억지스러운 피벗 설명을
 * 새로 찾지 않는다(사용자 지침: "가장 지혜로운 답을 찾아야 한다").
 */
function isChromaticDirectDominant(prev, ct, activeTonic, activeMode) {
  if (prev.root == null || (prev.kind !== 'triad' && prev.kind !== '7')) return false
  if (prev.quality !== 'maj' && prev.quality !== 'dom7') return false
  if (prev.bassPc !== prev.root) return false
  if ((prev.root - ct + 12) % 12 !== 7) return false
  if (prev.root === activeTonic && prev.quality === (activeMode === 'major' ? 'maj' : 'min')) return false
  return !isOrdinarySecondaryDominantTarget(activeTonic, prev.root, 'maj', activeMode)
}

/**
 * 공통화음·음계지속성 축과 완전히 독립적으로(그 축들의 결과를 모른 채) 전체를 처음
 * 조부터 순서대로 훑는다. 예전엔 다른 축이 이미 피벗으로 쓴 자리를 만나면 그 축의
 * (아직 확정되지 않았을 수도 있는) 결과를 보고 활성 조를 미리 동기화했는데, 그 다른
 * 축의 결과가 나중에 틀린 것으로 밝혀져 지워지는 경우(사용자 확인된 사고) 이 축까지
 * 덩달아 잘못된 활성 조를 보고 자기 자신의 정당한 발견을 놓쳐버렸다. 이제는 완전히
 * 독립적으로 스캔하고, 병합 이후 별도의 정합성 정리 단계에서 한꺼번에 조정한다.
 */
function detectChromaticModulations(chords, initialTonicPc, initialMode) {
  const n = chords.length
  const events = []
  if (n === 0) return events
  let activeTonic = initialTonicPc
  let activeMode = initialMode
  for (let i = 1; i < n; i++) {
    const prev = chords[i - 1]
    const curr = chords[i]
    if (prev.nct || curr.nct) continue // 표시 전용으로 채운 화음 — 전조 판정 근거로 안 씀
    if (curr.kind !== 'triad' || curr.root == null) continue
    if (curr.bassPc !== curr.root) continue
    const currQuality = curr.quality
    if (currQuality !== 'maj' && currQuality !== 'min') continue
    const targetMode = currQuality === 'maj' ? 'major' : 'minor'
    if (curr.root === activeTonic && targetMode === activeMode) continue
    if (!isChromaticDirectDominant(prev, curr.root, activeTonic, activeMode)) continue
    if (isOrdinarySecondaryDominantTarget(activeTonic, curr.root, currQuality, activeMode)) continue
    events.push({
      pivotIndex: i - 1,
      oldTonicPc: activeTonic,
      oldMode: activeMode,
      newTonicPc: curr.root,
      newMode: targetMode,
      source: 'chromatic',
    })
    activeTonic = curr.root
    activeMode = targetMode
  }
  return events
}

/**
 * 주어진 조성(tonicPc, mode) 기준으로 로마숫자·전위 라벨·2차기능을 만든다.
 * analyzeMeasure()의 기본 진행과, 공통화음 전조로 조성이 바뀐 이후 구간의
 * 로마숫자 재계산(detectCommonChordModulations)에서 공통으로 쓴다.
 */
function buildChordSymbol(tonicPc, rootPc, quality, kind, bassPc, mode = 'major') {
  const inv = inversionLabel(kind, bassPc, rootPc)
  let invFig = inversionFigure(kind, bassPc, rootPc)
  let roman = romanNumeral(tonicPc, rootPc, quality, kind, mode)
  if (mode === 'major') {
    if (
      kind === 'triad' &&
      quality === 'min' &&
      (rootPc - tonicPc + 12) % 12 === 0 &&
      inv === 'second inversion'
    ) {
      roman = CHORD_SYMBOL_I_MODE_MIX_B64
      invFig = ''
    }
    const MODE_MIX_LOWER = new Set(['iv', 'v', 'i'])
    if (kind === 'triad' && MODE_MIX_LOWER.has(roman) && invFig === '⁵₃') {
      invFig = '⁵♭₃'
    }
    const BORROW_FLAT_MAJOR_TRIADS = new Set(['bIII', 'bVI', 'bVII'])
    if (kind === 'triad' && BORROW_FLAT_MAJOR_TRIADS.has(roman) && invFig === '⁵₃') {
      invFig = '♭⁵₃'
    }
  }
  const tonicization = detectTonicization(tonicPc, rootPc, quality, mode)
  const chordSym = inlineInversionFigure(tonicization ?? roman, kind, invFig)
  return { chordSym, inv, tonicization }
}

// 근친조 후보 [오프셋, 목표 조 모드, 확정용 딸림화음이 "색채음 포함"이어야 하는지,
// 피벗을 나란한단조(parallel minor)의 변성화음 어휘에서도 찾을지].
// 장조 소스: baek/kostka 공통(V·IV). 단조 소스: kostka 5근친조(딸림조·버금딸림조 방향 +
// 나란한장조·자연단음계 VI·VII). ♭II(나폴리 방향, 반음 아래 장조)는 장·단조 공통으로 추가 —
// ♭II의 딸림화음(오프셋+8)이 원조에서 이미 VI(장조 vi/단조 VI)라서 구조적으로 항상
// "옛 조에도 있는 화음"일 수밖에 없다(♭II 자체가 반음 관계라 새 조 이끈음이 원조 으뜸음과
// 같은 음이 되기 때문 — 새 이끈음이 색채음이 되는 표준 판정이 성립하지 않음).
// 그래서 이 방향만 확정용 화음의 색채음 조건을 면제하고, 공통화음 존재 여부로만 판단한다.
// II(온음 위 장조, 딸림조의 딸림조 방향)도 추가 — 이 방향의 확정용 딸림화음(오프셋+9)은
// 원조 vi 자리인데 장조 vi는 항상 단3화음이라, 실제 나타나는 장3화음 딸림화음은 구조적으로
// 항상 색채음을 포함하게 되어 별도 면제 없이도 색채음 조건이 자연히 성립한다.
// ♭III(장조 나란한단조의 나란한장조 방향 — 예: C장조→Eb장조)은 baek §8 제2절
// "변성화음 전조"에 해당 — 온음계적 전조처럼 공통화음으로 전조하되, 그 공통화음이
// 원조 자체의 다이어토닉이 아니라 원조에서 빌려온 변성화음(모드믹스처, 예: ♭VI·iv)이라는
// 점만 다르다. ♭VI(Ab)·iv(Fm) 같은 변성화음이 Eb장조의 IV·ii와 그대로 겹치는데, 그
// 화음들은 C장조 자체에는 없고 C단조(나란한단조)에만 다이어토닉이므로
// pivotViaParallelMode=true로 나란한단조 쪽 변성화음도 피벗 후보에 포함시킨다.
const CLOSELY_RELATED_FROM_MAJOR = [
  [7, 'major', true, false], [5, 'major', true, false],
  [1, 'major', false, false], [2, 'major', true, false],
  [3, 'major', true, true],
]
const CLOSELY_RELATED_FROM_MINOR = [
  [7, 'minor', true, false], [5, 'minor', true, false], [3, 'major', true, false],
  [8, 'major', true, false], [10, 'major', true, false], [1, 'major', false, false],
]

function closelyRelatedCandidates(mode) {
  return mode === 'minor' ? CLOSELY_RELATED_FROM_MINOR : CLOSELY_RELATED_FROM_MAJOR
}

function parallelMode(mode) {
  return mode === 'minor' ? 'major' : 'minor'
}

// 전조 확정(confirmIndex) 이후 최소 이만큼(run 개수)은 지나야 그 조가 "확립됐다"고 보고
// 다음 피벗의 발판으로 쓸 수 있다 — 확정되자마자 곧바로 다음 조로 넘어가면(간격 0) 그 조는
// 진짜로 자리잡은 게 아니라 스쳐 지나가는 경유지일 뿐이므로 전조로 인정하지 않는다.
const MIN_SEGMENT_RUNS = 2

/**
 * §8-1 온음계적 전조(공통화음 전조) 검출 — backend/harmony_engine.py의
 * _detect_common_chord_modulations()와 동일 규칙(주석은 그쪽 참고).
 * chords: 마디·박 순서를 그대로 편 화음 목록. 각 항목 {root, quality, kind}.
 * 반환: [{pivotIndex, oldTonicPc, oldMode, newTonicPc, newMode, confirmIndex}, ...] (등장 순서).
 */
function detectCommonChordModulations(chords, initialTonicPc, initialMode = 'major') {
  const n = chords.length
  const events = []
  let activeTonic = initialTonicPc
  let activeMode = initialMode
  let segmentStart = 0
  // 지금까지 실제로 거쳐온 모든 조(활성 조 제외)의 이력 — "직전 조로 복귀"뿐 아니라 "몇
  // 단계 전에 있었던 조로 복귀"도 근친조 표에 없어도 허용하기 위함(예: Dm→F→G→Dm처럼
  // 두 단계 전의 원래 조로 돌아가는 경우). confirmIndex는 그 조가 정격종지로 확정된 시점
  // — 그 이전 자리는 아직 "그 조가 아니므로" 복귀 피벗으로 못 쓴다.
  const keyHistory = [{ tonicPc: initialTonicPc, mode: initialMode, confirmIndex: -1 }]
  let i = 1
  while (i < n) {
    const curr = chords[i]
    const prev = chords[i - 1]
    if (prev.root == null || curr.root == null || prev.nct || curr.nct) {
      // nct(한 성부만 비화성음이라 드롭 폴백으로 표시용으로만 채운 화음)는 확정용
      // 딸림·으뜸화음 후보에서 제외한다.
      i += 1
      continue
    }
    let matched = false
    let candidates = closelyRelatedCandidates(activeMode)
    // 지금까지 실제로 거쳐온 조(현재 활성 조 제외)로 "복귀"하는 방향은 근친조 표에 없어도
    // 허용한다 — Dm→F→G→Dm처럼 몇 단계 전의 원래 조로 돌아가는 경우도 포함(예: 변성화음/
    // 증6화음 등으로 잠시 다른 조들을 거친 뒤 원래 조로 정격종지 확정). 최근에 있었던
    // 조부터 먼저 시도한다. 표에 이미 같은 방향이 있거나 이미 후보에 추가된 방향이면
    // 중복으로 덧붙이지 않는다. 각 복귀 방향별로 그 조 자신이 확정된 시점(confirmIndex)도
    // 같이 기억해둔다 — 그 시점 이전은 아직 "그 조가 아니므로" 복귀 피벗으로 못 쓴다.
    const backConfirmByKey = new Map()
    for (let hi = keyHistory.length - 1; hi >= 0; hi--) {
      const hist = keyHistory[hi]
      if (hist.tonicPc === activeTonic && hist.mode === activeMode) continue
      const off = (hist.tonicPc - activeTonic + 12) % 12
      const keySig = `${off}:${hist.mode}`
      if (backConfirmByKey.has(keySig)) continue
      if (candidates.some(([cOff, cMode]) => cOff === off && cMode === hist.mode)) continue
      backConfirmByKey.set(keySig, hist.confirmIndex)
      candidates = [...candidates, [off, hist.mode, true, false]]
    }
    for (const [offset, targetMode, requireForeignConfirm, pivotViaParallelMode] of candidates) {
      const isBackCandidate = backConfirmByKey.has(`${offset}:${targetMode}`)
      const ct = (activeTonic + offset) % 12
      if (ct === activeTonic && targetMode === activeMode) continue
      if (!isVToTonic(prev.root, prev.quality, prev.kind, curr.root, curr.quality, curr.kind, ct)) {
        continue
      }
      // isVToTonic는 curr가 장/단 어느 품질의 으뜸화음이든 다 받아준다(다른 곳에서 조표
      // 무관하게 재사용하기 위함) — 그래서 여기서 직접 curr 품질이 이 후보가 노리는
      // targetMode와 실제로 맞는지 확인해야 한다. 안 그러면 예를 들어 "장조 목표(V)" 후보가
      // 우연히 같은 근음의 "단3화음"에도 잘못 매치되는 사고가 난다(근음만 같으면 장·단
      // 구분 없이 통과해버리는 구멍).
      if (curr.quality !== (targetMode === 'major' ? 'maj' : 'min')) continue
      // 정격종지로 새 조를 확정하려면 딸림화음·으뜸화음 둘 다 "근음위치"여야 한다 —
      // isVToTonic은 근음·성질만 보고 자리바꿈(전위)은 구분 못 해서, 그냥 지나가던
      // I⁶₃(1전위) 같은 화음도 근음이 "다른 조의 V" 자리와 겹치면 잘못 확정용 딸림/으뜸화음
      // 으로 오인할 수 있다(예: C장조 I⁶₃(bass=E, 전위)→F장조 IV(근음위치)를 "C가 F의 V,
      // 그 뒤가 F의 I" 식으로 잘못 매치하는 사고). bassPc를 모르면(레거시 호출 등) 판정을
      // 보류한다.
      if (curr.bassPc != null && curr.bassPc !== curr.root) continue
      if (prev.bassPc != null && prev.bassPc !== prev.root) {
        // 딸림7화음(V7)은 자리바꿈이어도 근음위치를 요구하지 않는다(사용자 지침) — 딸림7화음은
        // 그 자체로 이미 강한 색채적 정체성을 가져서(예: C장조 안에 "우연히" 나타난 다이어토닉
        // 화음의 전위가 다른 조의 V7과 근음·성질만 우연히 겹칠 위험이 사실상 없다) 위의 온음계
        // 삼화음 사례(C I⁶₃(bass=E)가 F의 V(C장3화음, 근음위치)와 근음·성질이 겹쳐 오탐 나던 것)
        // 와 달리 안전하다. 딸림"삼화음"은 계속 근음위치를 요구한다 — 그건 실제로 그 오탐 위험이 있다.
        if (prev.kind !== '7') continue
      }
      // 확정용 딸림화음의 "색채음 여부"는 항상 옛 조 자체(activeMode) 기준으로만 엄격하게
      // 판단한다 — pivotViaParallelMode(변성화음) 예외를 여기에도 적용하면 안 된다. 그
      // 예외는 "피벗 화음이 나란한단조에서 빌려온 화음이어도 된다"는 뜻이지, "확정용
      // 딸림화음이 나란한단조에 흔하면 색채음으로 안 친다"는 뜻이 아니다 — 예를 들어
      // C장조→Eb장조에서 확정용 Bb(장3화음)는 C단조의 자연스러운 bVII라 나란한단조
      // 예외를 적용하면 색채음 조건 자체가 무력화돼버린다.
      const oldKeyIsDiatonic = isDiatonicTriadInKey(activeTonic, prev.root, prev.quality, prev.kind, activeMode)
      if (requireForeignConfirm && oldKeyIsDiatonic) {
        continue // 확정용 딸림화음이 옛 조에도 흔한 다이어토닉 화음 — 색채음 없음, 전조 근거 부족
      }
      const isValidPivot = (p) => {
        const c = chords[p]
        if (c.nct) return false // 표시 전용으로 채운 화음 — 진짜 피벗 아님
        let pivotInOldKey = isDiatonicPivotChord(activeTonic, c.root, c.quality, c.kind, activeMode)
        if (pivotViaParallelMode) {
          pivotInOldKey = pivotInOldKey ||
            isDiatonicPivotChord(activeTonic, c.root, c.quality, c.kind, parallelMode(activeMode))
        }
        return pivotInOldKey && isDiatonicPivotChord(ct, c.root, c.quality, c.kind, targetMode)
      }

      const oldKeyStillFits = (p) => {
        // 공통화음은 옛 조의 스케일이 뚜렷이 바뀌기 전에 정해야 한다(사용자 지침) — 옛
        // 조에 없는 색채음이 낀 "3화음"이 먼저 나오면 그 지점부터는 이미 새 조 쪽으로
        // 기울었다고 보고 피벗 탐색 범위에서 제외한다. 7화음·증6화음 등은(kind!=='triad')
        // 다이어토닉 3화음 표로 판정할 수 없으니 판단을 보류한다(옛 조 딸림7화음처럼
        // 지극히 정상적인 화음까지 "이탈"로 오판하지 않기 위함).
        const c = chords[p]
        if (c.nct) return true // 표시 전용 화음 — 이탈 신호로 안 씀(판단 보류)
        if (c.kind !== 'triad') return true
        if (c.quality === 'maj' && (c.root - activeTonic + 12) % 12 === 1) return true // 나폴리(bII)는 옛 조 안에서도 흔한 색채화음 — 이탈 신호로 보지 않는다
        let fits = isDiatonicTriadInKey(activeTonic, c.root, c.quality, c.kind, activeMode)
        if (pivotViaParallelMode) {
          fits = fits || isDiatonicTriadInKey(activeTonic, c.root, c.quality, c.kind, parallelMode(activeMode))
        }
        return fits
      }

      // 피벗은 지금 활성 조(activeTonic) 자신이 확정된 시점(confirmIndex) *이후* 화음이어야
      // 한다 — segmentStart(활성 조의 피벗 시점)만으로 하한을 잡으면, 그 조가 확정되기도
      // 전인 "피벗~확정 사이 과도기" 화음까지 이번 전조의 피벗으로 잘못 골라버릴 수 있다
      // (아직 도착도 안 한 조에서 벌써 떠나는 꼴).
      const pivotFloor = events.length
        ? Math.max(segmentStart, events[events.length - 1].confirmIndex)
        : segmentStart

      let boundary = i - 1
      for (let q = pivotFloor; q < i - 1; q++) {
        if (!oldKeyStillFits(q)) {
          boundary = q
          break
        }
      }

      // 먼저 확정 종지 바로 앞(경계 이전 범위에서 가장 가까운 유효 피벗)을 찾는다.
      let pivotIndex = null
      for (let p = Math.min(i - 2, boundary - 1); p >= pivotFloor; p--) {
        if (isValidPivot(p)) {
          pivotIndex = p
          break
        }
      }
      if (pivotIndex == null && boundary < i - 1) {
        // 경계 이전 범위에 유효한 피벗이 하나도 없다면, 경계 판정 자체가 "일시적으로
        // 스쳐간 색채음 하나"였을 뿐 진짜 이탈이 아니었을 가능성이 있다(예: 곧 나올
        // 화음의 반딸림화음/부속화음 하나가 중간에 잠깐 낀 경우) — 그럴 땐 경계를
        // 무시하고 전체 범위에서 다시 가장 가까운 피벗을 찾는다.
        for (let p = i - 2; p >= pivotFloor; p--) {
          if (isValidPivot(p)) {
            pivotIndex = p
            break
          }
        }
      }
      // prev 혼자만으로 이미 "새 조로 직행하는 반음계적 전조"가 설명되는지 — 참이면
      // 반음계적 전조가 유력한 후보다(사용자 지침: 반음계적 전조는 새 조로 들어가는
      // V(7)-I가 뚜렷하고, 온음계적 전조는 공통화음을 거쳐 스무드하게 넘어간다 —
      // C-C#-D 같은 반음계적 진행이 없을 수 있다). 다만 prev 바로 앞(p=i-2)은 탐색에서
      // 뺀다 — 그 자리가 우연히 broad-fit 되는 건 진짜 준비가 아니라 직행 딸림화음
      // 바로 앞자리를 억지로 공통화음으로 우기는 것일 뿐이다. 그보다 더 앞(p<i-2)에
      // 진짜 이중기능 화음(예: C의 IV=Eb의 V/V)이 있다면 그건 우연이 아니라 진짜
      // 스무드한 준비이므로 여전히 공통화음 전조로 인정한다.
      const chromaticAvailable = isChromaticDirectDominant(prev, ct, activeTonic, activeMode)
      if (pivotIndex == null) {
        // 순수 다이어토닉(3화음·7화음) 범위에서 정말 아무 피벗도 못 찾았을 때만, 차용화음
        // (나란한단조)·부속화음(V/x)까지 넓게 인정해서 마지막으로 한 번 더 찾는다(사용자
        // 지침) — 이 넓은 기준을 처음부터 항상 쓰면 아무 장3화음/속7화음이나 "어딘가의
        // 부속화음"으로 걸려서 오탐이 폭증하므로, 반드시 순수 다이어토닉 피벗이 하나도
        // 없을 때의 최후 수단으로만 쓴다.
        const isValidPivotBroad = (p) => {
          const c = chords[p]
          if (c.nct) return false // 표시 전용으로 채운 화음 — 진짜 피벗 아님
          // 증6화음도 피벗이 될 수 있다(사용자 지침) — 다만 이 화음이 새 조(ct) 자신의
          // 예비딸림화음(impliedTonic === ct)이라는 것만으로는 확정 종지 바로 앞에 흔히
          // 놓이는 아무 증6화음이나 다 피벗으로 채가버린다(그 앞의 진짜 공통화음을
          // 밀어내는 오탐). 그래서 순수 다이어토닉/부속화음 피벗을 다 못 찾았을 때의
          // 최후 수단으로만 넣는다.
          if (c.aug6Tonic != null) {
            return c.aug6Tonic === ct
          }
          if (chromaticAvailable) {
            // 반음계적 전조로도 설명되는 상황(사용자 지침)이면 피벗 기준을 더 엄격히
            // 한다: (1) 옛 조·새 조 "둘 다"에서 그 조 자체의 기본 화음이어야 피벗으로
            // 인정한다 — 둘 중 어느 한쪽에서라도 부속화음(딸림7 등)으로만 설명되면
            // 피벗으로 인정하지 않고 반음계적 전조 쪽을 따른다(사용자 지침: "한쪽 중
            // 하나라도 기본 화음이 아니면 반음계적 전조로 인정"). 예: F=C의 IV(기본)=
            // Eb의 V/V(부속) — 새 조 쪽이 부속이라 이제는 피벗 자격이 없다. (2) prev
            // (직행 딸림화음)와 완전4/5도로 진짜 기능적으로 이어져야 한다(예비딸림→
            // 딸림).
            const oldPlain = isDiatonicPivotChord(activeTonic, c.root, c.quality, c.kind, activeMode)
            const newPlain = isDiatonicPivotChord(ct, c.root, c.quality, c.kind, targetMode)
            if (!(oldPlain && newPlain)) return false
            if (!fitsKeyBroadly(activeTonic, c.root, c.quality, c.kind, activeMode)) return false
            if (!fitsKeyBroadly(ct, c.root, c.quality, c.kind, targetMode)) return false
            const rel = (((prev.root - c.root) % 12) + 12) % 12
            if (rel !== 5 && rel !== 7) return false
            return true
          }
          if (
            fitsKeyBroadly(activeTonic, c.root, c.quality, c.kind, activeMode) &&
            fitsKeyBroadly(ct, c.root, c.quality, c.kind, targetMode)
          ) {
            return true
          }
          // 차용화음(나란한단조 빌림)도 피벗이 될 수 있다(사용자 지침) — 옛 조 쪽만
          // 나란한단조까지 넓게 인정한다. 새 조 쪽까지 넓히면 "장3화음이면 거의 다
          // 어딘가의 차용화음"이 되어 오탐이 폭증한다(이미 한 번 겪은 회귀: F(=C의 IV)가
          // G의 나란한단조(Gm)에서 빌린 bVII로도 매칭돼서 V/V 딸림화음 케이스를
          // 오염시켰다) — 그래서 대상 쪽은 반드시 자기 자신의(나란한단조 아닌)
          // 다이어토닉이어야 한다는 비대칭 조건을 둔다.
          if (c.kind === 'triad' || c.kind === '7') {
            const oldBorrowed = isDiatonicPivotChord(activeTonic, c.root, c.quality, c.kind, parallelMode(activeMode))
            const newOk = isDiatonicPivotChord(ct, c.root, c.quality, c.kind, targetMode)
            if (oldBorrowed && newOk) return true
          }
          return false
        }
        for (let p = i - 2; p >= pivotFloor; p--) {
          if (isValidPivotBroad(p)) {
            pivotIndex = p
            break
          }
        }
      }
      if (pivotIndex == null) continue
      // 변성화음 전조(pivotViaParallelMode)만: 거기서부터 계속 유효한 공통화음이 끊이지
      // 않고 이어지는 한(연속 구간) 더 앞으로 당긴다 — 전조는 그 연속 구간이 시작되는
      // 지점부터 이미 진행 중이었다고 봐야 하기 때문(사용자 지침: "전조의 시점을 더
      // 앞쪽에서 찾아야 한다"). V·IV 등 일반 근친조는 확장하지 않는다 — 그 방향은 옛
      // 조의 흔한 다이어토닉 화음(특히 으뜸화음 자체)이 거의 항상 새 조에서도
      // 다이어토닉이라, 끝없이 앞으로(흔히 곡 맨 앞 으뜸화음까지) 당겨져 버리는 부작용이
      // 있다 — 변성화음 방향은 공유 어휘가 좁아서 이 문제가 없다.
      if (pivotViaParallelMode) {
        while (pivotIndex > pivotFloor && isValidPivot(pivotIndex - 1)) {
          pivotIndex -= 1
        }
      }
      if (isBackCandidate && pivotIndex < events[events.length - 1].confirmIndex) {
        // "복귀" 방향은 취소-되돌림이 아니라 그냥 이 후보를 스킵한다 — 되돌림을 쓰면 방금
        // 확정한 진짜 전조(events 마지막 항목) 자체를 지워버리는 부작용이 있다. 피벗이 새
        // 조(activeTonic) 자신이 정격종지로 확정되기(confirmIndex) *전* 자리에 있다는 건
        // 그 새 조가 아직 제대로 확립되기도 전에 벌써 떠나려는 것이므로 무효 —
        // segmentStart(피벗 시점)가 아니라 confirmIndex(확정 시점) 기준으로 판단해야 한다
        // (피벗~확정 사이 구간은 아직 "진짜 그 조"가 아니라 전조 진행 중인 과도기).
        continue
      }
      if (!isBackCandidate && events.length > 0 && pivotIndex - events[events.length - 1].confirmIndex < MIN_SEGMENT_RUNS) {
        // 직전 전조가 확정되자마자(최소 지속 길이도 못 채우고) 곧바로 다음 피벗으로
        // 쓰이면, 그 조가 "확립됐다"고 보기엔 너무 짧다 — 그 전조를 취소하고 그 이전
        // 조성으로 되돌린 뒤(활성 조·segmentStart 복원) 같은 위치(i)를 재탐색한다.
        events.pop()
        keyHistory.pop()
        if (events.length > 0) {
          const prevEv = events[events.length - 1]
          activeTonic = prevEv.newTonicPc
          activeMode = prevEv.newMode
          segmentStart = prevEv.pivotIndex
        } else {
          activeTonic = initialTonicPc
          activeMode = initialMode
          segmentStart = 0
        }
        matched = true // matched=true just to trigger a retry of the same i below
        break
      }
      events.push({
        pivotIndex, oldTonicPc: activeTonic, oldMode: activeMode,
        newTonicPc: ct, newMode: targetMode, confirmIndex: i,
      })
      keyHistory.push({ tonicPc: ct, mode: targetMode, confirmIndex: i })
      activeTonic = ct
      activeMode = targetMode
      segmentStart = pivotIndex
      matched = true
      break
    }
    if (matched) continue // 같은 i에서 재검사(전조 확정 or 취소 후 되돌림 모두 해당)
    i += 1
  }
  return events
}

/** 백병동 표기: 장조는 대문자 조 이름(C), 단조는 소문자(c) — "Cm" 같은 m 접미 안 씀. */
function keyLetterForMode(pc, mode) {
  const letter = PC_TO_KEY_LETTER[pc] ?? '?'
  return mode === 'minor' ? letter.toLowerCase() : letter
}

/**
 * 공통화음 전조 검출 결과를 beat 엔트리에 반영(in-place).
 * 반음표 등 한 물리적 화음이 여러 박(beat) 샘플에 걸치는 경우, 전조 판정 자체는
 * "화음이 실제로 바뀌는 지점"(연속 동일 화음을 하나로 묶은 run) 단위로 한다 —
 * 같은 화음의 앞쪽 절반만 피벗/확정 라벨을 받고 뒤쪽 절반은 이전 라벨을 유지하는
 * 것을 막기 위함(같은 음표 위에 라벨 두 개가 겹쳐 찍히는 것 방지).
 */
function applyCommonChordModulations(
  flatChords, flatEntries, initialTonicPc, initialMode = 'major', theoryBase = 'baek',
  flatBeatPcs = null, beatsPerMeasure = 4,
) {
  const n = flatChords.length
  if (n === 0) return

  const runs = []
  const beatToRun = new Array(n).fill(0)
  for (let idx = 0; idx < n; idx++) {
    const c = flatChords[idx]
    const last = runs[runs.length - 1]
    if (
      last &&
      last.chord.root === c.root &&
      last.chord.quality === c.quality &&
      last.chord.kind === c.kind &&
      last.chord.bassPc === c.bassPc &&
      last.chord.aug6Tonic === c.aug6Tonic &&
      last.chord.nct === c.nct
    ) {
      last.members.push(idx)
    } else {
      runs.push({ chord: c, members: [idx] })
    }
    beatToRun[idx] = runs.length - 1
  }

  const collapsedChords = runs.map((r) => r.chord)
  const events = detectCommonChordModulations(collapsedChords, initialTonicPc, initialMode)

  const nRuns = runs.length
  const eventsByPivotRun = new Map(events.map((e) => [e.pivotIndex, e]))
  const tonicsByRun = new Array(nRuns).fill(initialTonicPc)
  const modesByRun = new Array(nRuns).fill(initialMode)
  let t = initialTonicPc
  let tm = initialMode
  for (let r = 0; r < nRuns; r++) {
    if (eventsByPivotRun.has(r)) {
      t = eventsByPivotRun.get(r).newTonicPc
      tm = eventsByPivotRun.get(r).newMode
    }
    tonicsByRun[r] = t
    modesByRun[r] = tm
  }

  if (flatBeatPcs && beatsPerMeasure > 0) {
    const nMeasures = Math.ceil(n / beatsPerMeasure)
    const measurePcSets = []
    const tonicByMeasure = []
    const modeByMeasure = []
    for (let mi = 0; mi < nMeasures; mi++) {
      const start = mi * beatsPerMeasure
      const end = Math.min(start + beatsPerMeasure, n)
      let pcs = new Set()
      for (let bi = start; bi < end; bi++) {
        for (const p of flatBeatPcs[bi]) pcs.add(p)
      }
      measurePcSets.push(pcs)
      const runIdx = beatToRun[start]
      tonicByMeasure.push(tonicsByRun[runIdx])
      modeByMeasure.push(modesByRun[runIdx])
    }

    const primaryPivotMeasures = new Set(
      [...eventsByPivotRun.keys()].map((r) => Math.floor(runs[r].members[0] / beatsPerMeasure)),
    )
    const scaleEvents = detectScalePersistenceModulations(
      measurePcSets, tonicByMeasure, modeByMeasure, primaryPivotMeasures,
    )
    for (const se of scaleEvents) {
      const prevMeasureLastBeat = se.measureStart * beatsPerMeasure - 1
      const pivotRun = prevMeasureLastBeat >= 0 ? beatToRun[prevMeasureLastBeat] : 0
      if (eventsByPivotRun.has(pivotRun)) continue
      eventsByPivotRun.set(pivotRun, {
        pivotIndex: pivotRun,
        oldTonicPc: se.oldTonicPc,
        oldMode: se.oldMode,
        newTonicPc: se.newTonicPc,
        newMode: se.newMode,
      })
    }

    if (eventsByPivotRun.size > events.length) {
      t = initialTonicPc
      tm = initialMode
      for (let r = 0; r < nRuns; r++) {
        if (eventsByPivotRun.has(r)) {
          t = eventsByPivotRun.get(r).newTonicPc
          tm = eventsByPivotRun.get(r).newMode
        }
        tonicsByRun[r] = t
        modesByRun[r] = tm
      }
    }
  }

  const nBeforeChromatic = eventsByPivotRun.size
  const chromaticEvents = detectChromaticModulations(collapsedChords, initialTonicPc, initialMode)
  for (const ce of chromaticEvents) {
    const r = ce.pivotIndex
    // 공통화음 축이 이 자리를 이미 피벗으로 썼어도, 반음계적 전조가 이 자리를
    // "짧고 확실한" 직행 딸림화음으로 독자적으로 찾아냈다면 그쪽을 우선한다 — 공통화음
    // 축은 이 자리를 훨씬 뒤에 있는(같은 조로 다시 들어오는) 별개의 확정 종지를 위한
    // 피벗으로 재사용하며 억지로 먼 거리를 이어붙인 경우가 있었다(사용자 확인된 사고:
    // Bb7이 두 번 나오는 곡에서, 뒤쪽 Bb7-Eb 정격종지를 위해 훨씬 앞쪽의 첫 Bb7을
    // 피벗으로 끌어써서, 4마디에서 이미 확정됐어야 할 Eb 전조가 7마디까지 늘어져
    // 표시됨). 반음계적 전조가 그 지점에서 이미 독자적으로 성립한다는 건 공통화음
    // 없이도 설명된다는 뜻이라, 더 단순한 설명을 우선한다.
    eventsByPivotRun.set(r, ce)
  }
  if (eventsByPivotRun.size > nBeforeChromatic) {
    t = initialTonicPc
    tm = initialMode
    for (let r = 0; r < nRuns; r++) {
      if (eventsByPivotRun.has(r)) {
        t = eventsByPivotRun.get(r).newTonicPc
        tm = eventsByPivotRun.get(r).newMode
      }
      tonicsByRun[r] = t
      modesByRun[r] = tm
    }
  }

  // 병합 후 정합성 정리: 서로 다른 축(공통화음·음계지속성·반음계적 전조)이 서로의
  // 결과를 모른 채 각자 독립적으로 전체를 스캔하다 보니, 이미 어떤 조로 들어와 있는데
  // "또 같은 조로 들어간다"는 두 번째(중복) 이벤트가 섞여 들어올 수 있다(사용자 지침으로
  // 실제 확인된 사고). 실제 활성 조 흐름을 순서대로 다시 훑어서, 새 조가 "이미 그
  // 조였던" 이벤트는 제거하고, 남는 이벤트의 oldTonicPc/oldMode도 실제 값으로 바로잡는다.
  {
    let realT = initialTonicPc
    let realTm = initialMode
    for (const r of [...eventsByPivotRun.keys()].sort((a, b) => a - b)) {
      const ev = eventsByPivotRun.get(r)
      if (ev.newTonicPc === realT && ev.newMode === realTm) {
        eventsByPivotRun.delete(r)
        continue
      }
      if (ev.oldTonicPc !== realT || ev.oldMode !== realTm) {
        ev.oldTonicPc = realT
        ev.oldMode = realTm
      }
      realT = ev.newTonicPc
      realTm = ev.newMode
    }
    t = initialTonicPc
    tm = initialMode
    for (let r = 0; r < nRuns; r++) {
      if (eventsByPivotRun.has(r)) {
        t = eventsByPivotRun.get(r).newTonicPc
        tm = eventsByPivotRun.get(r).newMode
      }
      tonicsByRun[r] = t
      modesByRun[r] = tm
    }
  }

  if (eventsByPivotRun.size === 0) return

  for (let r = 0; r < nRuns; r++) {
    if (eventsByPivotRun.has(r)) continue
    if (tonicsByRun[r] === initialTonicPc && modesByRun[r] === initialMode) continue
    const c = runs[r].chord
    if (c.root == null) continue
    const { chordSym, tonicization } = buildChordSymbol(tonicsByRun[r], c.root, c.quality, c.kind, c.bassPc, modesByRun[r])
    const chordSymThemed = applyTheoryChordSymbol(chordSym, theoryBase)
    for (const idx of runs[r].members) {
      flatEntries[idx].chordSymbol = chordSymThemed
      flatEntries[idx].tonicization = tonicization
    }
  }

  for (const [r, ev] of eventsByPivotRun) {
    const c = runs[r].chord
    if (c.root == null) continue
    const { chordSym: newRomanRaw } = buildChordSymbol(ev.newTonicPc, c.root, c.quality, c.kind, c.bassPc, ev.newMode)
    const newRoman = applyTheoryChordSymbol(newRomanRaw, theoryBase)
    const newLetter = keyLetterForMode(ev.newTonicPc, ev.newMode)
    let dual
    if (ev.source === 'chromatic') {
      // 반음계적 전조(사용자 지침): 공통화음이 없어서 "옛 조에서의 뜻"을 병기할 이유가
      // 없다 — 두 줄 피벗 박스 대신, "V7/Eb →" 처럼 부속화음 표기 스타일의 한 줄 +
      // 화살표로 구분한다(온음계적 전조의 두 줄 박스와 시각적으로 달라야 함).
      dual = `${newRoman}/${newLetter} →`
    } else {
      const { chordSym: oldRomanRaw } = buildChordSymbol(ev.oldTonicPc, c.root, c.quality, c.kind, c.bassPc, ev.oldMode)
      const oldRoman = applyTheoryChordSymbol(oldRomanRaw, theoryBase)
      // 옛 조는 이미 진행 중이던 조라 다시 이름을 밝힐 필요 없음 — 새 조만 이름 표기.
      dual = `${oldRoman}\n${newLetter}: ${newRoman}`
    }
    for (const idx of runs[r].members) {
      flatEntries[idx].chordSymbol = dual
      flatEntries[idx].tonicization = null
    }
    const nxtR = r + 1
    // 반음계적 전조는 "^" 이음줄 표시를 안 쓴다(사용자 지침 — 손글씨 예시: "V7/Eb →"
    // 화살표 자체가 다음 화음으로의 방향을 이미 나타내므로, 그 다음 화음은 그냥 새 조
    // 자기 자신의 평범한 로마숫자로 보여준다).
    if (ev.source !== 'chromatic' && nxtR < nRuns && !eventsByPivotRun.has(nxtR)) {
      for (const idx of runs[nxtR].members) {
        flatEntries[idx].chordSymbol = '^' + flatEntries[idx].chordSymbol
      }
    }
  }
}

function analyzeMeasure(tonicPc, sopranoT, altoT, tenorT, bassT, mode = 'major') {
  const pitches = [
    firstNotePitchFromVoice(sopranoT),
    firstNotePitchFromVoice(altoT),
    firstNotePitchFromVoice(tenorT),
    firstNotePitchFromVoice(bassT),
  ]
  const midis = pitches.map((p) => (p ? pitchToMidi(p) : null))
  const pcsSet = new Set(midis.filter((m) => m != null).map((m) => m % 12))
  const bassM = midis[3]
  const bassPc = bassM != null ? bassM % 12 : null

  if (pcsSet.size === 0) {
    return {
      chordSymbol: '—',
      inversion: '—',
      tonicization: null,
      voiceLeadingIssues: [],
      _rootPc: null,
      _quality: null,
      _kind: null,
      _midis: {
        soprano: midis[0], alto: midis[1], tenor: midis[2], bass: midis[3],
      },
      _pitches: {
        soprano: pitches[0], alto: pitches[1], tenor: pitches[2], bass: pitches[3],
      },
    }
  }

  if (pcsSet.size === 1) {
    // 근음만 있는 경우: 조성 내 음계급으로 품질 추론 (3음·5음 생략)
    const singlePc = [...pcsSet][0]
    const d = (singlePc - tonicPc + 12) % 12
    let quality
    if (mode === 'minor') {
      // 5도(offset 7)는 화성단음계 기본형(V, 장3화음)으로 추정
      const DIATONIC_Q_MINOR = { 0:'min', 2:'dim', 3:'maj', 5:'min', 7:'maj', 8:'maj', 10:'maj', 11:'dim' }
      quality = DIATONIC_Q_MINOR[d] ?? 'maj'
    } else {
      const diaMatch = MAJOR_DEGREES.find(([step]) => step === d)
      quality = diaMatch ? diaMatch[2] : 'maj'
    }
    const inv = inversionLabel('triad', bassPc, singlePc)
    const invFig = inversionFigure('triad', bassPc, singlePc)
    const roman = romanNumeral(tonicPc, singlePc, quality, 'triad', mode)
    const chordSym = inlineInversionFigure(roman, 'triad', invFig)
    const midisObj = { soprano: midis[0], alto: midis[1], tenor: midis[2], bass: midis[3] }
    const pitchesObj = { soprano: pitches[0], alto: pitches[1], tenor: pitches[2], bass: pitches[3] }
    return {
      chordSymbol: chordSym,
      inversion: inv,
      tonicization: null,
      voiceLeadingIssues: [],
      _rootPc: singlePc,
      _quality: quality,
      _kind: 'triad',
      _midis: midisObj,
      _pitches: pitchesObj,
    }
  }

  // 증6화음 우선 감지 (dom7과 음이름이 같아 표준 매칭이 오판하므로 먼저 처리)
  const pcToLetter = {}
  for (const p of pitches) {
    if (!p) continue
    const lm = String(p).match(/^([A-G])/)
    if (lm) pcToLetter[pitchToMidi(p) % 12] = lm[1]
  }
  const aug6 = detectAugmentedSixth(tonicPc, pcsSet, bassPc, pcToLetter, mode)
  if (aug6) {
    const [aug6Kind, aug6ImpliedTonic, aug6RootPc] = aug6
    const midisObj = { soprano: midis[0], alto: midis[1], tenor: midis[2], bass: midis[3] }
    const pitchesObj = { soprano: pitches[0], alto: pitches[1], tenor: pitches[2], bass: pitches[3] }
    return {
      chordSymbol: aug6Kind,
      inversion: inversionLabel('7', bassPc, aug6RootPc),
      tonicization: null,
      voiceLeadingIssues: [],
      _rootPc: null,
      _quality: null,
      _kind: null,
      _chordPcsOverride: new Set(pcsSet),
      _aug6Tonic: aug6ImpliedTonic,
      _midis: midisObj,
      _pitches: pitchesObj,
    }
  }

  const cands = matchChordPatterns(pcsSet)
  let picked = pickRootAndQuality(pcsSet, bassPc, cands)

  // 감5 생략 min7({0,3,10})은 vii∅7(반감7, 5음=감5 생략)과 이명동음 — 근음이 조 안에서
  // 이미 다이아토닉이면(예: C장조 iii=E) 정상 min7이지만, 근음이 반음계적(조 밖)이고
  // 근음+1반음이 2차기능 대상(ii·iii·IV·V·vi 등)이면 부속화음일 확률이 훨씬 높으므로
  // 반감7로 재해석한다. (부감7화음 vii°7/X는 이미 대칭 구조 보정을 따로 받는다.)
  if (picked && picked[2] === '7' && picked[1] === 'min7' && pcsSet.size === 3) {
    const omit5Root = picked[0]
    const rootStep = (omit5Root - tonicPc + 12) % 12
    const degreeSteps = new Set((mode === 'minor' ? MINOR_DEGREES : MAJOR_DEGREES).map((r) => r[0]))
    if (!degreeSteps.has(rootStep)) {
      const targetPc = (omit5Root + 1) % 12
      const secTargets = mode === 'minor' ? SECONDARY_TARGETS_MINOR : SECONDARY_TARGETS_MAJOR
      const offset = (targetPc - tonicPc + 12) % 12
      if (secTargets.some(([o]) => o === offset)) {
        picked = [omit5Root, 'half_dim7', '7']
      }
    }
  }

  // V7/V9에 11음(완전4)이 있으면 속11로 승격; V7+9만이면 속9
  if (picked && (
    (picked[2] === '7' && picked[1] === 'dom7')
    || (picked[2] === '9' && picked[1] === 'dom9')
  )) {
    const ninthPc = (picked[0] + 2) % 12
    const eleventhPc = (picked[0] + 5) % 12
    if (pcsSet.has(ninthPc) && pcsSet.has(eleventhPc)) {
      picked = [picked[0], 'dom11', '11']
    } else if (picked[2] === '7' && pcsSet.has(ninthPc)) {
      picked = [picked[0], 'dom9', '9']
    }
  }

  if (!picked) {
    picked = detectDominantEleventh(pcsSet, bassPc)
  }
  if (!picked) {
    picked = detectDominantNinth(pcsSet, bassPc)
  }

  let nctVoice = null
  if (!picked) {
    // 4성부 전체로는 화음이 안 풀려도, 한 성부(주로 소프라노)만 화성음이 아니고 나머지
    // 세 성부가 뚜렷한 3화음을 이루는 경우가 있다(사용자 지침 — 예: 알토·테너·베이스가
    // 그대로 Bb장3화음을 유지한 채 소프라노만 4분음표로 따로 움직이는 경과음/이웃음
    // 상황). 그럴 땐 "?"로 통째로 포기하지 말고, 한 성부씩 빼보면서 남은 세 성부가
    // 유효한 화음이 되는지 확인한다 — 베이스가 화성을 가장 안정적으로 규정하니
    // 소프라노·알토·테너 순으로 먼저 시도한다.
    const voiceNames = ['soprano', 'alto', 'tenor', 'bass']
    for (const dropIdx of [0, 1, 2, 3]) {
      if (midis[dropIdx] == null) continue
      if (dropIdx === 3) continue // 베이스 자체가 빠지면 판정 기준이 모호해져서 시도 안 함
      const reducedPcs = new Set(
        midis.filter((_m, j) => j !== dropIdx).filter((m) => m != null).map((m) => m % 12),
      )
      if (reducedPcs.size === pcsSet.size && [...reducedPcs].every((p) => pcsSet.has(p))) continue
      if (reducedPcs.size === 2) {
        // 5음이 생략된 불완전 3화음(근음+3음만 남는 경우) — 흔한 보이싱이므로
        // 3도 간격으로 추론한다(최상단 pcsSet.size===2 분기와 동일 로직).
        const pcsList2 = [...reducedPcs].sort((a, b) => a - b)
        const interval2 = ((pcsList2[1] - pcsList2[0]) % 12 + 12) % 12
        let twoRoot2 = null
        let twoQuality2 = null
        if (interval2 === 4) { twoRoot2 = pcsList2[0]; twoQuality2 = 'maj' }
        else if (interval2 === 8) { twoRoot2 = pcsList2[1]; twoQuality2 = 'maj' }
        else if (interval2 === 3) { twoRoot2 = pcsList2[0]; twoQuality2 = 'min' }
        else if (interval2 === 9) { twoRoot2 = pcsList2[1]; twoQuality2 = 'min' }
        else if (interval2 === 5 || interval2 === 7) {
          twoRoot2 = bassPc != null && reducedPcs.has(bassPc) ? bassPc : pcsList2[0]
          twoQuality2 = 'maj'
        }
        if (twoRoot2 != null && twoQuality2 != null) {
          picked = [twoRoot2, twoQuality2, 'triad']
          nctVoice = voiceNames[dropIdx]
          break
        }
        continue
      }
      if (reducedPcs.size < 3) continue
      const reducedCands = matchChordPatterns(reducedPcs)
      const reducedPicked = pickRootAndQuality(reducedPcs, bassPc, reducedCands)
      if (reducedPicked) {
        picked = reducedPicked
        nctVoice = voiceNames[dropIdx]
        break
      }
    }
  }

  if (!picked) {
    return {
      chordSymbol: '?',
      inversion: '—',
      tonicization: null,
      voiceLeadingIssues: [],
      _rootPc: null,
      _quality: null,
      _kind: null,
      _midis: {
        soprano: midis[0], alto: midis[1], tenor: midis[2], bass: midis[3],
      },
      _pitches: {
        soprano: pitches[0], alto: pitches[1], tenor: pitches[2], bass: pitches[3],
      },
    }
  }

  let [rootPc, quality, kind] = picked

  // 감7화음 루트 보정: 대칭 구조이므로 스펠링으로 이끔음을 식별.
  // 여러 이끔음 후보가 있으면 베이스=이끔음(근음위치)을 우선 — 예: D#°7→E = vii°7/iii
  // (고정 V>… 우선만 쓰면 F#도 G의 이끔으로 잡혀 vii°7/V로 오인됨).
  if (quality === 'dim7') {
    const DIATONIC_PC_LETTER = { 0:'C', 2:'D', 4:'E', 5:'F', 7:'G', 9:'A', 11:'B' }
    // V > vi > ii > IV > iii (장조) / V > VI > ii° > iv > III (단조) — 베이스 불일치 시 폴백
    const SEC_OFFSETS = mode === 'minor' ? [7, 8, 2, 5, 3] : [7, 9, 2, 5, 4]
    const isSpelledLeadingTone = (ltPc, targetPc) => {
      const la = pcToLetter[ltPc]
      const lb = DIATONIC_PC_LETTER[targetPc]
      return Boolean(la && lb && (_AUG6_LETTER_IDX[lb] - _AUG6_LETTER_IDX[la] + 7) % 7 === 1)
    }
    const candidates = []
    for (const off of SEC_OFFSETS) {
      const targetPc = (tonicPc + off) % 12
      const ltPc = (targetPc - 1 + 12) % 12
      if (!pcsSet.has(ltPc)) continue
      if (!isSpelledLeadingTone(ltPc, targetPc)) continue
      candidates.push(ltPc)
    }
    {
      const ltPc = (tonicPc + 11) % 12
      if (pcsSet.has(ltPc) && isSpelledLeadingTone(ltPc, tonicPc)) {
        candidates.push(ltPc)
      }
    }
    let foundRoot = null
    if (bassPc != null) {
      foundRoot = candidates.find((lt) => lt === bassPc) ?? null
    }
    if (foundRoot == null && candidates.length > 0) foundRoot = candidates[0]
    if (foundRoot != null) rootPc = foundRoot
  }

  const midisObj = { soprano: midis[0], alto: midis[1], tenor: midis[2], bass: midis[3] }
  const pitchesObj = { soprano: pitches[0], alto: pitches[1], tenor: pitches[2], bass: pitches[3] }
  const { chordSym, inv, tonicization } = buildChordSymbol(tonicPc, rootPc, quality, kind, bassPc, mode)

  return {
    chordSymbol: chordSym,
    inversion: inv,
    tonicization,
    voiceLeadingIssues: [],
    _nctVoice: nctVoice,
    _rootPc: rootPc,
    _quality: quality,
    _kind: kind,
    _midis: midisObj,
    _pitches: pitchesObj,
  }
}

// ─── 비화성음(NCT) 감지 ───────────────────────────────────────────────────────

/** 품질별 근음 기준 구성음 간격 (5음 생략 등은 부분집합이므로 포함 검사에 문제 없음) */
const QUALITY_CHORD_INTERVALS = {
  maj: [0, 4, 7],
  min: [0, 3, 7],
  dim: [0, 3, 6],
  aug: [0, 4, 8],
  dom7: [0, 4, 7, 10],
  min7: [0, 3, 7, 10],
  maj7: [0, 4, 7, 11],
  half_dim7: [0, 3, 6, 10],
  dim7: [0, 3, 6, 9],
  dom7s5: [0, 4, 8, 10],
  // 속9: NCT 판정 시 5음 포함·생략 모두 구성음으로 인정
  dom9: [0, 2, 4, 7, 10],
  // 속11: 3·5 포함·생략 모두 구성음으로 인정
  dom11: [0, 2, 4, 5, 7, 10],
}

/** 화음 구성음 피치클래스 집합. 화음을 못 정한 경우 null. */
function chordPcsSet(rootPc, quality) {
  if (rootPc == null || !quality) return null
  const ints = QUALITY_CHORD_INTERVALS[quality]
  if (!ints) return null
  return new Set(ints.map((i) => (rootPc + i) % 12))
}

/** 백엔드 결과는 beat가 1부터, 로컬 엔진은 0부터 → 0-index 정규화 오프셋 */
function beatBaseOffsetOf(beatBlocks) {
  const nums = beatBlocks.map((b) => Number(b.beat)).filter(Number.isFinite)
  return nums.length > 0 && Math.min(...nums) >= 1 ? 1 : 0
}

function segPitchToMidi(pitch) {
  const m = String(pitch).match(PITCH_RE)
  if (!m) return null
  const pc = (((LETTER_PC[m[1]] + (ACC_SEMITONES[m[2] ?? ''] ?? 0)) % 12) + 12) % 12
  return (parseInt(m[3], 10) + 1) * 12 + pc
}

/**
 * beat block 배열에 각 성부의 NCT 타입을 추가한다.
 * - UN: 상행 이웃음 (upper neighbor)
 * - LN: 하행 이웃음 (lower neighbor)
 * - DN: 이중 이웃음 (double neighbor) — 쌍으로 표시
 * - PT: 경과음 (passing tone)
 */
function detectNonChordTones(beatBlocks, segS, segA, segT, segB) {
  if (!beatBlocks.length) return
  const allSegs = { soprano: segS, alto: segA, tenor: segT, bass: segB }
  const beatOff = beatBaseOffsetOf(beatBlocks)

  // 세그먼트 시작 시점이 속한 beat block (0-index 정규화 span 포함 검사)
  const findBlockIdx = (beat) =>
    beatBlocks.findIndex((b) => {
      const start = Number(b.beat) - beatOff
      const end = (b.beatEnd != null ? Number(b.beatEnd) : Number(b.beat) + 1) - beatOff
      return beat >= start - BEAT_SAMPLE_EPS && beat < end - BEAT_SAMPLE_EPS
    })

  // 화음 구성음이면 비화성음이 아님 (화음을 못 정한 블록은 선율 모양만으로 판정)
  const isChordToneAt = (bi, midi) => {
    const pcs = beatBlocks[bi]?._chordPcs
    return pcs != null && pcs.has(((midi % 12) + 12) % 12)
  }

  // 해당 시점 t에 걸린 세그먼트(쉼표 포함) — 세그먼트 경계(t===직전 세그먼트의 end===다음
  // 세그먼트의 start)에서는 '방금 시작한' 세그먼트를 우선한다(start가 가장 늦은 것을 선택).
  const segmentAt = (segments, t) => {
    let best = null
    for (const s of segments) {
      if (s.start <= t + BEAT_SAMPLE_EPS && (best == null || s.start > best.start)) best = s
    }
    return best
  }

  // beat block 인덱스 → 그 블록의 표본 시점(t). 전체 화음이 매칭 실패("?")해도
  // 개별 성부의 협화/불협 판정은 계속할 수 있도록, 나머지 세 성부만으로 화음을 다시 추정한다.
  const sampleTimeOf = (bi) => {
    const b = beatBlocks[bi]
    if (!b) return null
    const start = Number(b.beat) - beatOff
    const end = (b.beatEnd != null ? Number(b.beatEnd) : Number(b.beat) + 1) - beatOff
    return (start + end) / 2
  }

  /**
   * 특정 성부를 제외한 나머지 세 성부만으로 화음 구성음 집합을 추정.
   * 전체 4성 화음 매칭이 (얹힌 비화성음 때문에) 실패해도, 나머지 세 성부만 보면
   * 대개 정상적인 3화음/자리바꿈이 식별된다. 실패하면 null(판정 불가).
   * bi가 주어지면, 그 박에서 이미 비화성음으로 판정된 다른 성부는 기준 화음 추정에서 제외한다
   * (이미 장식음으로 밝혀진 성부를 "진짜 화성"으로 오인하지 않도록).
   */
  const otherThreeChordPcs = (voice, t, bi, minRawPcs = 2) => {
    const pcs = new Set()
    for (const v of ['soprano', 'alto', 'tenor', 'bass']) {
      if (v === voice) continue
      if (bi != null && beatBlocks[bi]?.nct?.[v] != null) continue
      const seg = segmentAt(allSegs[v], t)
      if (!seg || seg.isRest || !seg.pitch) continue
      const m = segPitchToMidi(seg.pitch)
      if (m != null) pcs.add(((m % 12) + 12) % 12)
    }
    if (pcs.size < minRawPcs) return null   // 서로 다른 음이 너무 적으면(전위 우연 일치 위험) 판정 보류
    // 이 박의 4성 전체 화음이 이미 정상적으로 식별돼 있고(_chordPcs), 그 화음이 지금
    // 3성부만으로 뽑은 pcs를 전부 포함하면(=3성부가 그 화음의 부분집합이면) 그 이미 알려진
    // 화음을 그대로 쓴다 — 3성부만으로 다시 처음부터 매칭을 시도하면 우연히 다른(엉뚱한)
    // 화음으로 잘못 읽혀서, 정작 뺀 성부(예: 베이스=화음의 근음)가 "협화음이 아니다"로
    // 오판되는 사고가 난다(사용자 확인: Bb7의 D-F-Ab만 보고 D°(감3화음)로 오인해서, 진짜
    // 근음인 베이스 Bb를 이스케이프톤으로 잘못 표시).
    const knownPcs = bi != null ? beatBlocks[bi]?._chordPcs : null
    if (knownPcs != null && [...pcs].every((pc) => knownPcs.has(pc))) {
      return knownPcs
    }
    const candidates = matchChordPatterns(pcs)
    if (candidates.length === 0) return null
    const allowed = new Set()
    for (const [rootPc, q] of candidates) {
      const cs = chordPcsSet(rootPc, q)
      if (cs) for (const pc of cs) allowed.add(pc)
    }
    return allowed.size > 0 ? allowed : null
  }

  /** 나머지 세 성부 기준으로 판정 불가하면(추정 실패) 불협으로 단정하지 않는다(보수적). */
  const isDissonantAgainstOthers = (voice, t, midi, bi, minRawPcs = 2) => {
    const pcs = otherThreeChordPcs(voice, t, bi, minRawPcs)
    if (pcs == null) return false
    return !pcs.has(((midi % 12) + 12) % 12)
  }

  /**
   * 나머지 세 성부가 전부 이 시점 이전부터 이어져 오고 있는지(= 새로 어택하지 않았는지) 확인.
   * 하3성이 긴 음가로 유지되는 동안 한 성부만 짧은 음가로 움직이는 경우,
   * 그 순간에 우연히 화음 구성음과 일치하더라도 "경과음/이웃음" 해석이 함께 성립할 수 있다.
   * (다른 성부가 같은 시점에 새로 어택하면 실질적 화성 교체로 보고 제외)
   */
  const otherVoicesSustained = (voice, t) => {
    for (const v of ['soprano', 'alto', 'tenor', 'bass']) {
      if (v === voice) continue
      const seg = segmentAt(allSegs[v], t)
      if (!seg || seg.isRest || seg.start >= t - BEAT_SAMPLE_EPS) return false
    }
    return true
  }

  const markNct = (bi, voice, segStart, type) => {
    if (!beatBlocks[bi].nct) beatBlocks[bi].nct = {}
    if (!beatBlocks[bi].nctStart) beatBlocks[bi].nctStart = {}
    beatBlocks[bi].nct[voice] = type
    beatBlocks[bi].nctStart[voice] = segStart
  }

  for (const voice of ['soprano', 'alto', 'tenor', 'bass']) {
    const vSegs = allSegs[voice].filter((s) => !s.isRest && s.pitch)

    // 단일 이웃음 / 경과음: 연속 3개 세그먼트 스캔
    for (let j = 1; j < vSegs.length - 1; j++) {
      const m0 = segPitchToMidi(vSegs[j - 1].pitch)
      const m1 = segPitchToMidi(vSegs[j].pitch)
      const m2 = segPitchToMidi(vSegs[j + 1].pitch)
      if (m0 == null || m1 == null || m2 == null) continue
      if (m1 === m0) continue                          // 동음 반복은 제외
      if (Math.abs(m1 - m0) > 2 || Math.abs(m1 - m2) > 2) continue  // 순차 아님

      const bi = findBlockIdx(vSegs[j].start)
      if (bi < 0) continue
      // 진짜 화음 구성음이면(그 박의 화음이 실제로 식별된 경우) 경과/이웃음으로 보지 않는다.
      // 예: IV6/3 → V7 로 화성이 바뀌는 박에서, 새 화음의 7음(예: Bb7의 Ab)에 도착한 음은
      // 그 자체로 정당한 화음음이므로 "경과음"으로 이중 표시하면 안 된다.
      if (isChordToneAt(bi, m1)) continue

      if (m0 === m2) {
        // 같은 음으로 귀환 → 이웃음
        markNct(bi, voice, vSegs[j].start, m1 > m0 ? 'UN' : 'LN')
      } else if ((m1 > m0 && m2 > m1) || (m1 < m0 && m2 < m1)) {
        // 같은 방향으로 순차 진행 → 경과음
        markNct(bi, voice, vSegs[j].start, 'PT')
      }
    }

    // 이중 이웃음: 연속 4개 세그먼트 스캔 (N0→N1→N2→N0)
    for (let j = 0; j < vSegs.length - 3; j++) {
      const m0 = segPitchToMidi(vSegs[j].pitch)
      const m1 = segPitchToMidi(vSegs[j + 1].pitch)
      const m2 = segPitchToMidi(vSegs[j + 2].pitch)
      const m3 = segPitchToMidi(vSegs[j + 3].pitch)
      if (m0 == null || m1 == null || m2 == null || m3 == null) continue
      if (m0 !== m3) continue   // 원음으로 귀환해야 함
      if (m1 === m0 || m2 === m0) continue
      if (Math.abs(m1 - m0) > 2 || Math.abs(m2 - m1) > 2 || Math.abs(m3 - m2) > 2) continue
      // 한 쪽은 위, 한 쪽은 아래
      if (!((m1 > m0 && m2 < m0) || (m1 < m0 && m2 > m0))) continue

      const bi1 = findBlockIdx(vSegs[j + 1].start)
      const bi2 = findBlockIdx(vSegs[j + 2].start)
      if (bi1 < 0 || bi2 < 0) continue
      // 두 이웃음 모두 각자 박 화음의 비구성음이어야 함
      if (isChordToneAt(bi1, m1) || isChordToneAt(bi2, m2)) continue
      markNct(bi1, voice, vSegs[j + 1].start, 'DN')
      markNct(bi2, voice, vSegs[j + 2].start, 'DN')
    }

    const tagged = (bi) => beatBlocks[bi]?.nct?.[voice] != null
    const chordKnownAt = (bi) => beatBlocks[bi]?._chordPcs != null

    // 계류음/지연음: 예비음과 같은 음을 유지(또는 재타음)한 채 해결로 넘어간다.
    // 얹힌 음 때문에 그 순간 전체 화음 매칭이 실패("?")할 수 있으므로, 불협 여부는
    // 나머지 세 성부만으로 추정한 화음을 기준으로 판정한다. 하행 해결 → SUS, 상행 해결 → RET.
    for (let j = 1; j < vSegs.length - 1; j++) {
      const m0 = segPitchToMidi(vSegs[j - 1].pitch)
      const m1 = segPitchToMidi(vSegs[j].pitch)
      const m2 = segPitchToMidi(vSegs[j + 1].pitch)
      if (m0 == null || m1 == null || m2 == null) continue
      if (m1 !== m0) continue                          // 예비음과 같은 음(유지/재타음)이어야 함
      if (Math.abs(m2 - m1) === 0 || Math.abs(m2 - m1) > 2) continue  // 해결은 순차여야 함

      const bi1 = findBlockIdx(vSegs[j].start)
      const bi2 = findBlockIdx(vSegs[j + 1].start)
      if (bi1 < 0 || bi2 < 0 || tagged(bi1)) continue
      if (!chordKnownAt(bi2) || !isChordToneAt(bi2, m2)) continue     // 해결음은 화성음이어야 함
      if (!isDissonantAgainstOthers(voice, vSegs[j].start, m1, bi1)) continue  // 유지 구간이 실제로 불협이어야 함

      markNct(bi1, voice, vSegs[j].start, m2 < m1 ? 'SUS' : 'RET')
    }

    // 도약 후 순차 해결(어포지아투라) / 순차 후 도약(경과이탈음, 이스케이프톤)
    for (let j = 1; j < vSegs.length - 1; j++) {
      const m0 = segPitchToMidi(vSegs[j - 1].pitch)
      const m1 = segPitchToMidi(vSegs[j].pitch)
      const m2 = segPitchToMidi(vSegs[j + 1].pitch)
      if (m0 == null || m1 == null || m2 == null) continue
      if (m1 === m0) continue

      const bi1 = findBlockIdx(vSegs[j].start)
      if (bi1 < 0 || tagged(bi1)) continue
      if (!isDissonantAgainstOthers(voice, vSegs[j].start, m1, bi1)) continue  // 비화성음이어야 함

      const bi2 = findBlockIdx(vSegs[j + 1].start)
      if (bi2 < 0 || !chordKnownAt(bi2) || !isChordToneAt(bi2, m2)) continue  // 해결(도착)은 화성음

      const leapIn = Math.abs(m1 - m0) > 2
      const stepIn = Math.abs(m1 - m0) <= 2
      const leapOut = Math.abs(m2 - m1) > 2
      const stepOut = Math.abs(m2 - m1) <= 2 && m2 !== m1
      const reversed = Math.sign(m1 - m0) !== Math.sign(m2 - m1)

      if (leapIn && stepOut && reversed) {
        markNct(bi1, voice, vSegs[j].start, 'APP')        // 어포지아투라: 도약 진입 → 순차 해결
      } else if (stepIn && leapOut && reversed) {
        markNct(bi1, voice, vSegs[j].start, 'ET')          // 이스케이프톤: 순차 진입 → 도약 이탈
      }
    }

    // 예상음: 다음 화성으로 바뀌기 직전에 그 화성의 화성음을 먼저 눌러버리는 경우.
    // 직전 음과 다른 새 어택이어야 하고, 나머지 세 성부는 아직 이전 화성에 머물러 있어야
    // (이 성부만 "혼자 먼저" 움직인 것이어야) 진짜 예상음으로 본다.
    for (let j = 1; j < vSegs.length; j++) {
      const mPrev = segPitchToMidi(vSegs[j - 1].pitch)
      const m1 = segPitchToMidi(vSegs[j].pitch)
      if (m1 == null || mPrev === m1) continue
      const bi1 = findBlockIdx(vSegs[j].start)
      if (bi1 < 0 || tagged(bi1)) continue
      if (!otherVoicesSustained(voice, vSegs[j].start)) continue  // 혼자 먼저 움직였어야 함
      if (!isDissonantAgainstOthers(voice, vSegs[j].start, m1, bi1)) continue  // 현재는 비화성음이어야 함
      const bi2 = bi1 + 1
      if (bi2 >= beatBlocks.length) continue
      if (!chordKnownAt(bi2) || !isChordToneAt(bi2, m1)) continue   // 다음 화성엔 화성음(같은 음)
      markNct(bi1, voice, vSegs[j].start, 'ANT')
    }
  }

  // 오르간포인트(페달): 한 성부가 한 음을 재타음 없이 길게(3박 이상) 유지하는 동안,
  // 그 사이 최소 한 시점에서 나머지 세 성부 기준 화음과 불협이 되는 경우(=주변 화성이 움직였다는 증거).
  for (const voice of ['soprano', 'alto', 'tenor', 'bass']) {
    for (const s of allSegs[voice]) {
      if (s.isRest || !s.pitch) continue
      const startIdx = findBlockIdx(s.start)
      // 세그먼트가 덮는 마지막 박의 '중간 지점'으로 조회 — 경계값에서 부동소수 오차로
      // 다음 블록으로 새는 것을 피한다 (엔진 다른 곳의 표본 방식과 동일).
      const endIdx = findBlockIdx(Math.max(s.start, s.end - 0.5))
      if (startIdx < 0 || endIdx < 0 || endIdx - startIdx < 2) continue  // 최소 3개 박 블록에 걸쳐야 함
      const m1 = segPitchToMidi(s.pitch)
      if (m1 == null) continue
      let dissonantSomewhere = false
      for (let bi = startIdx + 1; bi < endIdx; bi++) {
        const t = sampleTimeOf(bi)
        // 페달은 구조적 주장이 크므로, 나머지 세 성부 중 최소 3개 서로 다른 음이 실제로
        // 남아있을 때만(=전위 2음 우연 일치가 아닐 때만) 불협 증거로 인정한다.
        if (t != null && isDissonantAgainstOthers(voice, t, m1, bi, 3)) { dissonantSomewhere = true; break }
      }
      if (!dissonantSomewhere) continue
      for (let bi = startIdx; bi <= endIdx; bi++) {
        if (beatBlocks[bi]?.nct?.[voice] != null) continue  // 기존 태그 우선
        markNct(bi, voice, s.start, 'PED')
      }
    }
  }
}

/**
 * 이미 완성된 분석 결과(백엔드·프론트 공용)에 NCT 정보를 덧붙인다.
 * payload 구조: { measures: [{soprano, alto, tenor, bass}, ...], timeSignature }
 */
export function enrichWithNct(analysisResult, payload) {
  if (!analysisResult?.measures?.length) return analysisResult
  const bppRaw = getBeatsPerMeasure(payload?.timeSignature || '4/4')
  const beatsPerMeasure = Number.isFinite(bppRaw) && bppRaw > 0 ? bppRaw : 4
  const rawMeasures = payload?.measures || []
  const [tonicPc, ncMode] = parseKey(payload?.key || 'C')

  for (let i = 0; i < analysisResult.measures.length; i++) {
    const m = rawMeasures[i] ?? {}
    const segS = voiceSegmentsFromTokens(m.soprano || [], beatsPerMeasure)
    const segA = voiceSegmentsFromTokens(m.alto    || [], beatsPerMeasure)
    const segT = voiceSegmentsFromTokens(m.tenor   || [], beatsPerMeasure)
    const segB = voiceSegmentsFromTokens(m.bass    || [], beatsPerMeasure)
    const beatBlocks = analysisResult.measures[i].beats
    if (!beatBlocks?.length) continue

    // 백엔드 응답에는 화음 근음·품질 정보가 없으므로 로컬 엔진으로 구성음을 재계산
    const beatOff = beatBaseOffsetOf(beatBlocks)
    for (const b of beatBlocks) {
      const start = Number(b.beat) - beatOff
      const end = (b.beatEnd != null ? Number(b.beatEnd) : Number(b.beat) + 1) - beatOff
      const sampleTime = (start + end) / 2
      const block = analyzeMeasure(
        tonicPc,
        notesAtSampleTime(segS, sampleTime),
        notesAtSampleTime(segA, sampleTime),
        notesAtSampleTime(segT, sampleTime),
        notesAtSampleTime(segB, sampleTime),
        ncMode,
      )
      b._chordPcs = block._chordPcsOverride ?? chordPcsSet(block._rootPc, block._quality)
    }

    // 경과음·이웃음 판정(detectNonChordTones)은 이 마디 세그먼트만 보고 앞뒤 세 음을
    // 스캔하는데, 그 음이 마디 경계에 걸쳐 있으면(예: 이 마디 마지막 음 → 다음 마디
    // 첫 음으로 이어지는 경과음) 이 마디 세그먼트만으로는 다음 음을 못 봐서 판정을
    // 놓친다(사용자 확인된 사고). 앞뒤 마디의 경계 음 하나씩을 문맥으로만 곁들여서
    // (실제 마킹 대상은 findBlockIdx가 이 마디 범위 밖이라 자동으로 걸러짐) 경계를
    // 넘나드는 경과음/이웃음도 잡히게 한다.
    const lastNonRestToken = (tokens) => {
      const list = Array.isArray(tokens) ? tokens : []
      for (let k = list.length - 1; k >= 0; k--) {
        const p = parseVoiceToken(list[k])
        if (!p.isRest && p.pitch) return p.pitch
      }
      return null
    }
    const firstNonRestToken = (tokens) => {
      const list = Array.isArray(tokens) ? tokens : []
      for (const raw of list) {
        const p = parseVoiceToken(raw)
        if (!p.isRest && p.pitch) return p.pitch
      }
      return null
    }
    const prevM = rawMeasures[i - 1]
    const nextM = rawMeasures[i + 1]
    const withBoundary = (seg, voiceKey) => {
      const out = [...seg]
      if (prevM) {
        const pitch = lastNonRestToken(prevM[voiceKey])
        if (pitch) out.unshift({ start: -1, end: 0, isRest: false, pitch })
      }
      if (nextM) {
        const pitch = firstNonRestToken(nextM[voiceKey])
        if (pitch) out.push({ start: beatsPerMeasure, end: beatsPerMeasure + 1, isRest: false, pitch })
      }
      return out
    }
    detectNonChordTones(
      beatBlocks,
      withBoundary(segS, 'soprano'),
      withBoundary(segA, 'alto'),
      withBoundary(segT, 'tenor'),
      withBoundary(segB, 'bass'),
    )
    for (const b of beatBlocks) delete b._chordPcs
  }
  return analysisResult
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {{ key?: string, theoryBase?: string, timeSignature?: string, measures?: Array<Record<string, unknown>> }} payload
 * @returns {{ measures: Array<Record<string, unknown>>, theoryBase: string, theoryRules: object }}
 */
export function analyzeHarmonyPayload(payload) {
  const key = payload.key || 'C'
  const theoryBase = normalizeTheoryBase(payload.theoryBase || 'baek')
  const rules = getTheoryRules(theoryBase)
  const [tonicPc, mode] = parseKey(key)
  const measuresRaw = payload.measures || []
  const bppRaw = getBeatsPerMeasure(payload.timeSignature || '4/4')
  const beatsPerMeasure = Number.isFinite(bppRaw) && bppRaw > 0 ? bppRaw : 4

  const outMeasures = []
  let prevMidis = null
  let prevPitches = null
  let prevEntry = null
  let prevRoot = null
  let prevQuality = null
  let prevKind = null
  const flatChords = []
  const flatEntries = []
  const flatBeatPcs = []

  for (let i = 0; i < measuresRaw.length; i++) {
    const m = measuresRaw[i]
    const measureNo = m.measure ?? i + 1
    const segS = voiceSegmentsFromTokens(m.soprano || [], beatsPerMeasure)
    const segA = voiceSegmentsFromTokens(m.alto || [], beatsPerMeasure)
    const segT = voiceSegmentsFromTokens(m.tenor || [], beatsPerMeasure)
    const segB = voiceSegmentsFromTokens(m.bass || [], beatsPerMeasure)

    const beatBlocks = []
    const measureIssues = []

    for (let b = 0; b < beatsPerMeasure; b++) {
      const t = Math.min(b + 0.5, beatsPerMeasure - BEAT_SAMPLE_EPS)

      const block = analyzeMeasure(
        tonicPc,
        notesAtSampleTime(segS, t),
        notesAtSampleTime(segA, t),
        notesAtSampleTime(segT, t),
        notesAtSampleTime(segB, t),
        mode,
      )
      let {
        _midis: currMidis,
        _pitches: currPitches,
        _rootPc: currRootPc,
        _quality: currQuality,
        _kind: currKind,
        _aug6Tonic: currAug6Tonic,
        _nctVoice: currNctVoice,
        ...rest
      } = block
      delete rest.voiceLeadingIssues
      let ptOverride = false
      let advancedNote = null
      if (
        rest.chordSymbol === '?' &&
        prevEntry != null &&
        prevRoot != null &&
        prevMidis != null &&
        prevMidis.bass != null &&
        currMidis.bass != null &&
        ((currMidis.bass % 12) + 12) % 12 === ((prevMidis.bass % 12) + 12) % 12
      ) {
        // 베이스가 그대로인 채(화성이 안 바뀐 채) 이 박만 화음으로 안 풀리면, 경과음·
        // 이웃음 같은 비화성음일 가능성이 높다(사용자 지침) — 직전 화음을 그대로
        // 이어받고 PT로 표시한다("?"로 뜬금없이 끊기지 않게).
        rest.chordSymbol = prevEntry.chordSymbol
        rest.inversion = prevEntry.inversion ?? rest.inversion
        rest.tonicization = prevEntry.tonicization ?? null
        currRootPc = prevRoot
        currQuality = prevQuality
        currKind = prevKind
        ptOverride = true
        advancedNote = 'PT'
      } else if (currNctVoice != null) {
        // 한 성부만 비화성음이고 나머지 세 성부가 뚜렷한 화음을 이루는 경우
        // (analyzeMeasure의 드롭 폴백) — root/quality/kind는 그대로 살려둔다(뒤에서
        // 활성 조 기준으로 로마숫자를 다시 계산하는 일반 재라벨링 패스가 이 박도
        // 정상적으로 처리하게 하기 위함). 대신 flatChords에 별도 nct 표시를 남겨서,
        // 공통화음·반음계적 전조 탐색 쪽에서는 이 박을 피벗·확정화음 후보로 쓰지
        // 않도록 막는다.
        advancedNote = 'PT'
      }
      if (rest.chordSymbol && !ptOverride) {
        rest.chordSymbol = applyTheoryChordSymbol(rest.chordSymbol, theoryBase)
      }
      rest.voicing = voicingPosition(currMidis, theoryBase)
      let vIssues = voiceRangeIssues(currMidis, theoryBase)
      vIssues = vIssues.concat(upperVoiceSpacingIssues(currMidis))
      if (prevMidis != null) {
        const isCadence = isVToTonic(
          prevRoot, prevQuality, prevKind,
          currRootPc, currQuality, currKind, tonicPc,
        )
        if (prevKind === '11' && prevQuality === 'dom11' && isCadence) {
          vIssues.push('속11화음: V 내부 해결 후 I 권장')
        }
        vIssues = vIssues.concat(parallelIssues(prevMidis, currMidis, theoryBase, { isAuthenticCadence: isCadence }))
        const hiddenIssue = hiddenParallelIssue(prevMidis, currMidis)
        if (hiddenIssue != null) vIssues.push(hiddenIssue)
        if (rules.check_outer_voice_convergence) {
          const convergenceIssue = outerVoiceConvergenceIssue(prevMidis, currMidis)
          if (convergenceIssue != null) vIssues.push(convergenceIssue)
        }
        vIssues = vIssues.concat(unequalFifthsIssue(
          prevMidis, currMidis,
          prevRoot, prevQuality, prevKind,
          currRootPc, currQuality, currKind,
          tonicPc,
          theoryBase,
        ))
        const commonToneIssue = commonToneRetentionIssue(prevMidis, currMidis)
        if (commonToneIssue != null) vIssues.push(commonToneIssue)
        vIssues = vIssues.concat(augmentedMelodicIntervalIssues(prevPitches, currPitches))
        if (rules.check_cross_relation) {
          vIssues = vIssues.concat(crossRelationIssues(prevPitches, currPitches))
        }
      }
      const curr7Voices = chordalSeventhVoices(currMidis, currRootPc, currQuality, currKind)
      for (const v of curr7Voices) {
        if (!isAcceptableSeventhPrepare(prevMidis?.[v], currMidis?.[v])) {
          const label = VOICE_LABEL_KO[v] ?? v
          vIssues.push(`${label} 7음 준비 위반: not good`)
        }
      }
      const tonicIssue = tonicTriadRootDoublingIssue(tonicPc, currRootPc, currQuality, currKind, currMidis)
      if (tonicIssue != null) vIssues.push(tonicIssue)
      const fifthIssue = fifthDoublingIssue(currRootPc, currQuality, currKind, rest.inversion, currMidis)
      if (fifthIssue != null) vIssues.push(fifthIssue)
      const n6Issue = neapolitanThirdDoublingIssue(
        tonicPc, currRootPc, currQuality, currKind, rest.inversion, currMidis,
      )
      if (n6Issue != null) vIssues.push(n6Issue)
      const thirdIssue = triadThirdOmissionIssue(currRootPc, currQuality, currKind, currMidis)
      if (thirdIssue != null) vIssues.push(thirdIssue)
      const ninthThird = dominantNinthThirdOmission(currRootPc, currQuality, currKind, currMidis)
      if (ninthThird != null) vIssues.push(ninthThird.issue)
      vIssues.push(...dominantEleventhOmissionIssues(currRootPc, currQuality, currKind, currMidis))
      measureIssues.push(...vIssues)
      const currEntry = {
        ...rest,
        beat: b + 1,
        analyzedBy: 'python',
        advancedAnalysis: advancedNote,
        voiceLeadingIssues: vIssues,
        omittedThird: Boolean(ninthThird?.omittedThird) || undefined,
        _midisRef: currMidis,
        _seventhVoices: curr7Voices,
      }
      beatBlocks.push(currEntry)
      flatChords.push({
        root: currRootPc,
        quality: currQuality,
        kind: currKind,
        bassPc: currMidis?.bass != null ? ((currMidis.bass % 12) + 12) % 12 : null,
        aug6Tonic: currAug6Tonic ?? null,
        nct: currNctVoice != null,
      })
      flatEntries.push(currEntry)
      flatBeatPcs.push(new Set(
        Object.values(currMidis || {}).filter((v) => v != null).map((v) => ((v % 12) + 12) % 12),
      ))
      if (prevEntry) {
        for (const v of prevEntry._seventhVoices ?? []) {
          if (!isValidSeventhResolution(prevEntry?._midisRef?.[v], currMidis?.[v])) {
            const label = VOICE_LABEL_KO[v] ?? v
            const msg = `${label} 7음 해결 위반: not good`
            prevEntry.voiceLeadingIssues.push(msg)
            measureIssues.push(msg)
          }
        }
      }
      prevMidis = currMidis
      prevPitches = currPitches
      prevEntry = currEntry
      prevRoot = currRootPc
      prevQuality = currQuality
      prevKind = currKind
    }

    for (const b of beatBlocks) {
      delete b._midisRef
      delete b._seventhVoices
    }

    outMeasures.push({
      measure: measureNo,
      beats: beatBlocks,
      chordSymbol: beatBlocks[0]?.chordSymbol ?? '—',
      inversion: beatBlocks[0]?.inversion ?? '—',
      analyzedBy: 'python',
      advancedAnalysis: null,
      tonicization: beatBlocks[0]?.tonicization ?? null,
      voicing: beatBlocks[0]?.voicing ?? null,
      voiceLeadingIssues: measureIssues,
    })
  }

  applyCommonChordModulations(flatChords, flatEntries, tonicPc, mode, theoryBase, flatBeatPcs, beatsPerMeasure)
  for (const om of outMeasures) {
    if (om.beats.length) {
      om.chordSymbol = om.beats[0].chordSymbol
      om.tonicization = om.beats[0].tonicization ?? null
    }
  }

  return { measures: outMeasures, theoryBase, theoryRules: rules }
}
